import React, { useState, useEffect, useMemo, useRef } from 'react';
import styles from './FilterSection.module.scss';
import {
  Flex,
  MenuTrigger,
  ActionButton,
  Button,
  View,
  Text,
  Item,
  Menu,
  // Checkbox,
  Divider,
  SubmenuTrigger
} from '@adobe/react-spectrum';
import MenuCheckbox from './MenuCheckbox';
// import Button from '@react/react-spectrum/Button';
import ChevronRight from '@spectrum-icons/workflow/ChevronRight';
import Select, { SelectOption } from '@react/react-spectrum/Select';
import { useSelector } from 'react-redux';
import {
  selectCategories,
  selectEvents,
  selectProductGroups,
  selectAudiences,
  selectReleases,
  selectFeatures,
  selectProductFilter,
  selectHierarchyFilters,
  selectLanesTiers,
  selectStatus,
  selectCategoryToProductMap,
  selectCategoryProductFilter,
  selectGenAiFilter,
  selectRevenueImpactFilter,
  selectHome,
  selectMomentTypeFilter,
  selectSavedViewApplyToken
} from 'app/pages/Home/slice/selectors';
import type { RootState } from 'types';
import { useHomeSlice } from 'app/pages/Home/slice';
import CheckmarkIcon20 from 'app/assets/svgIcons/CheckmarkIcon20';
import Close from '@react/react-spectrum/Icon/Close';
import { useDispatch } from 'react-redux';
import {
  saveUserPreferences,
  getUserPreferences,
  removeUserPreferenceField,
  type UserPreferences
} from 'utils/helpers';
import { shouldPreferUrlOverLocalPreferences } from 'utils/sharedViewMode';
import { Selection } from '@react-types/shared';
import { selectIsAuthenticated } from 'app/pages/Layout/slice/selectors';
import { CategoryProductFilter } from './CategoryProductFilter';
import { EventsFilter } from './EventsFilter';
import { StatusFilter } from './StatusFilter';
import { MomentTypeFilter } from './MomentTypeFilter';
import { getRoadmapMomentTypeMenuOptions } from './momentTypeRoadmapFilter';
import { useClearMustWinState } from 'app/hooks/useClearMustWinState';
import { useRestoreMustWinState } from 'app/hooks/useRestoreMustWinState';

// Module-level (not component-instance) — restore the filter snapshot from
// localStorage once per browser session, not on every mount. NewFilter lives
// inside RoadmapHeader, which remounts on every Milestones<->Roadmap
// transition (separate top-level pages). restorationAttempted (component
// state, below) still flips on every mount so the downstream "step 2"
// effects that depend on it keep working unchanged — only the actual
// localStorage read+dispatch block is restricted to session-once, since that
// part was re-asserting stale filters right after a Saved View (or the
// shared-preferences restore) had just correctly set them, silently
// clearing the Saved View's active highlight via the divergence check.
let hasRestoredFilterPreferences = false;

// Helper function to generate count display for filter sections
const getFilterCountDisplay = (
  selectedItems: any,
  totalOptions: any[],
  allKey: string = 'all'
): string => {
  if (!totalOptions || totalOptions.length === 0) return '';

  if (selectedItems instanceof Set) {
    if (selectedItems.size === 0) return '';
    return selectedItems.has(allKey)
      ? ` (${totalOptions.length})`
      : ` (${Array.from(selectedItems).filter(s => s !== allKey).length})`;
  }

  if (Array.isArray(selectedItems)) {
    if (selectedItems.length === 0) return '';
    return selectedItems.includes(allKey)
      ? ` (${totalOptions.length})`
      : ` (${selectedItems.filter(s => s !== allKey).length})`;
  }

  return '';
};

interface NewFilterProps {
  hideEventsFilter?: boolean;
  /** End of Life (EOL) moment type is roadmap-only (hidden on milestones grid report). */
  hideEolMomentType?: boolean;
  /** Include PCO and EOL in moment-type filter menu (milestones grid report). */
  milestonesGridMomentTypeMenu?: boolean;
  excludeLanesTiers?: string[]; // Array of tier keys to exclude (e.g., ['key-dates', 'events'])
  customLanesTiersOptions?: Array<{ key: string; label: string }>; // Custom lanes/tiers options to use instead of API data
  /** Whether the current route is Roadmap/My Stuff (vs. Milestones) — gates the
   * shared-preference restore in STEP 1 below, since that restore is meaningless
   * (and racy against Saved View/My Stuff transitions) while on Milestones. */
  isRoadmapRoute?: boolean;
}

export function NewFilter({
  hideEventsFilter = false,
  hideEolMomentType = false,
  milestonesGridMomentTypeMenu = false,
  excludeLanesTiers = [],
  customLanesTiersOptions,
  isRoadmapRoute = true
}: NewFilterProps = {}) {
  const { actions } = useHomeSlice();
  const dispatch = useDispatch();
  const clearMustWinState = useClearMustWinState();
  const restoreMustWinState = useRestoreMustWinState();
  const [groupOptions, setGroupOptions] = useState<any[]>([]);
  const [audienceOptions, setAudienceOptions] = useState<any[]>([]);
  const [eventOptions, setEventOptions] = useState<any[]>([]);
  const [featureOptions, setFeatureOptions] = useState<any[]>([]);

  const [selectedProductGroups, setSelectedProductGroups] =
    React.useState<Selection>(new Set([]));
  const [selectedAudiences, setSelectedAudiences] = React.useState<Selection>(
    new Set([])
  );

  const [selectedeventsLabel, setSelectedeventsLabel] =
    React.useState<Selection>(new Set([]));
  const [selectedFeaturesValues, setSelectedFeaturesValues] =
    React.useState<Selection>(new Set([]));
  const [eventValue, setEventValue] = useState<string | string[]>('');

  // Lanes/Tiers filter state
  const lanesTiers = useSelector(selectLanesTiers);
  const [selectedFilterOptions, setSelectedFilterOptions] =
    React.useState<Selection>(new Set());

  // Status filter state
  const [selectedStatusOptions, setSelectedStatusOptions] =
    React.useState<Selection>(new Set());

  const [selectedMomentTypeOptions, setSelectedMomentTypeOptions] =
    React.useState<Selection>(new Set());

  const handleFilterChange = (selection: Selection) => {
    // ✅ Clear Must Win dropdown special UI when regular filters are modified
    clearMustWinState();

    let finalSelection: Set<string>;
    const hasAllOption = lanesTiersFilterOptions.some(opt => opt.key === 'all');

    if (selection === 'all') {
      // Normalize "all" to current option keys (e.g. from Redux/preferences)
      const allKeys = new Set(
        lanesTiersFilterOptions.map(option => option.key as string)
      );
      setSelectedFilterOptions(allKeys);
      finalSelection = allKeys;
    } else if (selection instanceof Set) {
      const currentSelection = new Set(
        Array.from(selection).map(s => s as string)
      );
      const previousSelection =
        selectedFilterOptions instanceof Set
          ? selectedFilterOptions
          : new Set();

      if (hasAllOption) {
        const allJustSelected =
          currentSelection.has('all') && !previousSelection.has('all');
        const allJustDeselected =
          !currentSelection.has('all') && previousSelection.has('all');

        if (allJustSelected) {
          const allKeys = new Set(
            lanesTiersFilterOptions.map(option => option.key as string)
          );
          setSelectedFilterOptions(allKeys);
          finalSelection = allKeys;
        } else if (allJustDeselected) {
          const emptySelection = new Set<string>();
          setSelectedFilterOptions(emptySelection);
          finalSelection = emptySelection;
        } else {
          const nonAllOptions = lanesTiersFilterOptions.filter(
            option => option.key !== 'all'
          );
          const allIndividualSelected = nonAllOptions.every(option =>
            currentSelection.has(option.key as string)
          );

          if (allIndividualSelected && nonAllOptions.length > 0) {
            currentSelection.add('all');
            setSelectedFilterOptions(currentSelection);
            finalSelection = currentSelection;
          } else {
            currentSelection.delete('all');
            setSelectedFilterOptions(currentSelection);
            finalSelection = currentSelection;
          }
        }
      } else {
        // No "All" option (e.g. Must Wins: L1, L2, L3 only) – use selection as-is, strip invalid keys
        const validKeys = new Set(
          Array.from(currentSelection).filter(k =>
            lanesTiersFilterOptions.some(opt => opt.key === k)
          )
        );
        setSelectedFilterOptions(validKeys);
        finalSelection = validKeys;
      }
    } else {
      setSelectedFilterOptions(selection);
      finalSelection = new Set<string>();
    }
    dispatch(actions.setSelectedLanesTiers(Array.from(finalSelection)));
    saveUserPreferences({ lanesTiers: Array.from(finalSelection) });
  };

  const handleStatusChange = (selection: Selection) => {
    // ✅ Clear Must Win dropdown special UI when regular filters are modified
    clearMustWinState();

    // CSME-113550: 'Completed' is a distinct preference (hideCompletedItems), not part of
    // the risk-level selectedStatus array. Detect its toggle here — comparing against the
    // currently-shown checked state (!hideCompletedItems) — and route it to hideCompletedItems
    // (inverted: checked = shown = hideCompletedItems false) instead of letting it fall into
    // the risk-level selection logic below. 'completed' is stripped out either way so it never
    // leaks into selectedStatus.
    let workingSelection: Selection = selection;
    if (selection instanceof Set) {
      const rawSelection = new Set(Array.from(selection).map(s => s as string));
      const wasCompletedShown = !homeState.hideCompletedItems;
      const isCompletedShown = rawSelection.has('completed');

      if (isCompletedShown !== wasCompletedShown) {
        const newHideCompletedItems = !isCompletedShown;
        dispatch(actions.setHideCompletedItems(newHideCompletedItems));
        saveUserPreferences({ hideCompletedItems: newHideCompletedItems });
      }

      rawSelection.delete('completed');
      workingSelection = rawSelection;
    }

    let finalSelection: Set<string>;

    if (workingSelection === 'all') {
      const allKeys = new Set(
        statusFilterOptions.map(option => option.key as string)
      );
      setSelectedStatusOptions(allKeys);
      finalSelection = allKeys;
    } else if (workingSelection instanceof Set) {
      const currentSelection = new Set(
        Array.from(workingSelection).map(s => s as string)
      );
      const previousSelection =
        selectedStatusOptions instanceof Set
          ? selectedStatusOptions
          : new Set();

      const allJustSelected =
        currentSelection.has('all') && !previousSelection.has('all');
      const allJustDeselected =
        !currentSelection.has('all') && previousSelection.has('all');

      if (allJustSelected) {
        const allKeys = new Set(
          statusFilterOptions.map(option => option.key as string)
        );
        setSelectedStatusOptions(allKeys);
        finalSelection = allKeys;
      } else if (allJustDeselected) {
        // When "All" is deselected, clear all selections
        const emptySelection = new Set<string>();
        setSelectedStatusOptions(emptySelection);
        finalSelection = emptySelection;
      } else {
        const nonAllOptions = statusFilterOptions.filter(
          option => option.key !== 'all'
        );
        const allIndividualSelected = nonAllOptions.every(option =>
          currentSelection.has(option.key as string)
        );

        if (allIndividualSelected && nonAllOptions.length > 0) {
          currentSelection.add('all');
          setSelectedStatusOptions(currentSelection);
          finalSelection = currentSelection;
        } else {
          currentSelection.delete('all');
          setSelectedStatusOptions(currentSelection);
          finalSelection = currentSelection;
        }
      }
    } else {
      setSelectedStatusOptions(workingSelection);
      finalSelection = new Set<string>();
    }
    dispatch(actions.setSelectedStatus(Array.from(finalSelection)));
    saveUserPreferences({ status: Array.from(finalSelection) });
  };

  // Track whether initial localStorage restoration is complete
  const [isInitialRestorationComplete, setIsInitialRestorationComplete] =
    useState(false);
  // Track whether restoration has been attempted to prevent multiple runs
  const [restorationAttempted, setRestorationAttempted] = useState(false);
  // Track if we've already updated valid options for current restoration cycle
  const [validOptionsUpdated, setValidOptionsUpdated] = useState(false);
  // Track when user explicitly clicks "All" to prevent auto-correction interference
  const [userClickedAll, setUserClickedAll] = useState<{
    filter: string;
    action: 'select' | 'deselect';
  } | null>(null);
  // Use ref for more persistent tracking that doesn't get cleared by re-renders
  const userClickedAllRef = useRef<{
    filter: string;
    action: 'select' | 'deselect';
    timestamp: number;
  } | null>(null);

  // Computed selectedKeys that automatically show "All" as selected when all individual items are selected
  const computedProductGroupKeys = useMemo(() => {
    if (!(selectedProductGroups instanceof Set)) return selectedProductGroups;

    const enabledIndividualOptions = groupOptions.filter(
      opt => opt.value !== 'all' && !opt.disabled
    );
    const enabledIndividualValues = enabledIndividualOptions.map(
      opt => opt.value
    );

    // Check if all enabled individual options are selected
    const allIndividualSelected =
      enabledIndividualValues.length > 0 &&
      enabledIndividualValues.every(val => selectedProductGroups.has(val));

    // If all individual options are selected, ensure "all" is included in the UI
    if (allIndividualSelected && !selectedProductGroups.has('all')) {
      return new Set([...Array.from(selectedProductGroups), 'all']);
    }

    return selectedProductGroups;
  }, [selectedProductGroups, groupOptions]);

  const computedAudienceKeys = useMemo(() => {
    if (!(selectedAudiences instanceof Set)) return selectedAudiences;

    const enabledIndividualOptions = audienceOptions.filter(
      opt => opt.value !== 'all' && !opt.disabled
    );
    const enabledIndividualValues = enabledIndividualOptions.map(
      opt => opt.value
    );

    // Check if all enabled individual options are selected
    const allIndividualSelected =
      enabledIndividualValues.length > 0 &&
      enabledIndividualValues.every(val => selectedAudiences.has(val));

    // If all individual options are selected, ensure "all" is included in the UI
    if (allIndividualSelected && !selectedAudiences.has('all')) {
      return new Set([...Array.from(selectedAudiences), 'all']);
    }

    return selectedAudiences;
  }, [selectedAudiences, audienceOptions]);

  const computedFeatureKeys = useMemo(() => {
    if (!(selectedFeaturesValues instanceof Set)) return selectedFeaturesValues;

    const individualOptions = featureOptions.filter(opt => opt.value !== 'all');
    const individualValues = individualOptions.map(opt => opt.value);

    // Check if all individual options are selected
    const allIndividualSelected =
      individualValues.length > 0 &&
      individualValues.every(val => selectedFeaturesValues.has(val));

    // If all individual options are selected, ensure "all" is included in the UI
    if (allIndividualSelected && !selectedFeaturesValues.has('all')) {
      return new Set([...Array.from(selectedFeaturesValues), 'all']);
    }

    return selectedFeaturesValues;
  }, [selectedFeaturesValues, featureOptions]);

  // Memoized count displays for performance optimization
  const segmentCountDisplay = useMemo(() => {
    return getFilterCountDisplay(selectedAudiences, audienceOptions);
  }, [selectedAudiences, audienceOptions]);

  const productGroupCountDisplay = useMemo(() => {
    return getFilterCountDisplay(selectedProductGroups, groupOptions);
  }, [selectedProductGroups, groupOptions]);

  const surfaceCountDisplay = useMemo(() => {
    return getFilterCountDisplay(selectedFeaturesValues, featureOptions);
  }, [selectedFeaturesValues, featureOptions]);

  const eventsCountDisplay = useMemo(() => {
    return getFilterCountDisplay(selectedeventsLabel, eventOptions, 'All');
  }, [selectedeventsLabel, eventOptions]);

  const options = [
    // { label: 'All', value: 'all' },
    // { label: 'None', value: 'none' }
  ];
  const productGroups = useSelector(selectProductGroups);
  const audiences = useSelector(selectAudiences);
  const categories = useSelector(selectCategories);
  const releases = useSelector(selectReleases);
  const selectedEvents = useSelector(selectEvents);
  const features = useSelector(selectFeatures);
  const productFilter = useSelector(selectProductFilter);
  const hierarchyFilters = useSelector(selectHierarchyFilters);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const categoryToProductMap = useSelector(selectCategoryToProductMap);
  const categoryProductFilter = useSelector(selectCategoryProductFilter);
  const genAiFilter = useSelector(selectGenAiFilter);
  const homeState = useSelector(selectHome);
  const status = useSelector(selectStatus);
  const momentTypeFilter = useSelector(selectMomentTypeFilter);
  const savedViewApplyToken = useSelector(selectSavedViewApplyToken);
  const rsdMomentTypes = useSelector(
    (s: RootState) => s.rsdForms?.momentTypes ?? []
  );

  // Gen AI filter state
  const [selectedGenAiFilters, setSelectedGenAiFilters] =
    React.useState<Selection>(new Set([]));

  // Revenue Impact filter state
  const revenueImpactFilter = useSelector(selectRevenueImpactFilter);
  const [selectedRevenueImpactFilters, setSelectedRevenueImpactFilters] =
    React.useState<Selection>(new Set([]));

  // Count display for Category-Product hierarchical filter
  const categoryProductCountDisplay = useMemo(() => {
    const categoryCount = categoryProductFilter.selectedCategories.length;
    const productCount = categoryProductFilter.selectedProducts.length;

    const categoryText =
      categoryCount > 0 ? `Category (${categoryCount})` : 'Category';
    const productText =
      productCount > 0 ? `Product (${productCount})` : 'Product';

    return `${categoryText} / ${productText}`;
  }, [
    categoryProductFilter.selectedCategories,
    categoryProductFilter.selectedProducts
  ]);

  const lanesTiersFilterOptions = useMemo(() => {
    // If custom options are provided, use them directly
    if (customLanesTiersOptions && customLanesTiersOptions.length > 0) {
      return customLanesTiersOptions;
    }

    // Otherwise, derive from API data
    const options: { key: string; label: string }[] = [];
    if (releases?.releaseScheduled) {
      const types = releases.releaseScheduled.map(
        (item: any) => item.type as string
      );
      const uniqueTypes: string[] = Array.from(new Set(types));
      uniqueTypes.forEach(type => {
        const key = type.toLowerCase().replace(/\s+/g, '-');
        // Exclude specified lane/tier options
        if (!excludeLanesTiers.includes(key)) {
          options.push({
            key,
            label: type
          });
        }
      });
    }
    return options;
  }, [releases?.releaseScheduled, excludeLanesTiers, customLanesTiersOptions]);

  // Status filter options - static options for now
  const statusFilterOptions = useMemo(() => {
    return [
      { key: 'risk-level-low', label: 'Risk Level : Low' },
      { key: 'risk-level-medium', label: 'Risk Level : Medium' },
      { key: 'risk-level-high', label: 'Risk Level : High' },
      { key: 'in-planning', label: 'In Planning' }
    ];
  }, []);

  const momentTypeMenuOptions = useMemo(
    () =>
      getRoadmapMomentTypeMenuOptions(rsdMomentTypes, {
        includeEol: !hideEolMomentType,
        milestonesGridMenu: milestonesGridMomentTypeMenu
      }),
    [rsdMomentTypes, hideEolMomentType, milestonesGridMomentTypeMenu]
  );

  // Track previous audiences to prevent unnecessary API calls
  const prevAudiencesRef = useRef<string>('');

  // Ref-based guards: only sync from Redux when value actually changed (avoids loop when selector returns new ref each render)
  const lastLanesTiersRef = useRef<string>('');
  const lastStatusRef = useRef<string>('');
  const lastMomentTypeRef = useRef<string>('');
  const lastProductGroupsRef = useRef<string>('');
  const lastAudiencesRef = useRef<string>('');

  const lanesTiersCountDisplay = useMemo(() => {
    // Only count selections that exist in current options so count matches visible options
    // (e.g. when Must Wins active and only L1/L2 are shown, or when switching views)
    if (lanesTiersFilterOptions.length === 0) return '';
    const optionKeys = new Set(lanesTiersFilterOptions.map(o => o.key));
    const filteredSelection = new Set(
      Array.from(selectedFilterOptions).filter(k => optionKeys.has(k as string))
    );
    return getFilterCountDisplay(filteredSelection, lanesTiersFilterOptions);
  }, [selectedFilterOptions, lanesTiersFilterOptions]);

  const statusCountDisplay = useMemo(() => {
    // Only count selections that exist in current options so count matches visible options
    const optionKeys = new Set(statusFilterOptions.map(o => o.key));
    const filteredSelection = new Set(
      Array.from(selectedStatusOptions).filter(k => optionKeys.has(k as string))
    );
    // 'Completed' lives outside statusFilterOptions/selectedStatusOptions (it's the
    // independent hideCompletedItems preference) but is still a checkable item in the
    // same Status menu, so it needs to be folded into the badge count when checked.
    const count =
      filteredSelection.size + (!homeState.hideCompletedItems ? 1 : 0);
    return count === 0 ? '' : ` (${count})`;
  }, [
    selectedStatusOptions,
    statusFilterOptions,
    homeState.hideCompletedItems,
    status.selectedStatus
  ]);

  // CSME-113550: 'Completed' is checked (shown as selected) whenever hideCompletedItems is
  // false, unioned with the risk-level selectedStatus keys — independent preference, same menu.
  const combinedStatusKeys = useMemo(() => {
    const base =
      selectedStatusOptions instanceof Set
        ? new Set(Array.from(selectedStatusOptions).map(k => String(k)))
        : new Set<string>();
    if (!homeState.hideCompletedItems) {
      base.add('completed');
    }
    return base;
  }, [selectedStatusOptions, homeState.hideCompletedItems]);

  const momentTypeCountDisplay = useMemo(() => {
    // Only count selections visible in the current menu (e.g. exclude EOL on milestones)
    if (momentTypeMenuOptions.length === 0) return '';
    const optionKeys = new Set(momentTypeMenuOptions.map(o => String(o.key)));
    const filteredSelection = new Set(
      Array.from(
        selectedMomentTypeOptions instanceof Set
          ? selectedMomentTypeOptions
          : []
      ).filter(k => optionKeys.has(String(k)))
    );
    return getFilterCountDisplay(filteredSelection, momentTypeMenuOptions);
  }, [selectedMomentTypeOptions, momentTypeMenuOptions]);

  const handleMomentTypeChange = (selection: Selection) => {
    const validKeys = new Set(momentTypeMenuOptions.map(o => String(o.key)));

    let finalSelection: Set<string>;
    if (selection === 'all') {
      finalSelection = new Set(momentTypeMenuOptions.map(o => String(o.key)));
    } else if (selection instanceof Set) {
      finalSelection = new Set(
        Array.from(selection)
          .map(s => String(s))
          .filter(k => validKeys.has(k))
      );
    } else {
      finalSelection = new Set();
    }

    setSelectedMomentTypeOptions(finalSelection);
    const arr = Array.from(finalSelection);
    dispatch(actions.setSelectedMomentTypes(arr));
    saveUserPreferences({ momentTypeFilters: arr });
  };

  // Sync local state with Redux state for Lanes/Tiers (only call setState when value changed to avoid infinite loop)
  useEffect(() => {
    const key = (lanesTiers.selectedLanesTiers as string[])
      .slice()
      .sort()
      .join(',');
    if (lastLanesTiersRef.current === key) return;
    lastLanesTiersRef.current = key;
    if (lanesTiers.selectedLanesTiers.length > 0) {
      setSelectedFilterOptions(
        new Set(lanesTiers.selectedLanesTiers as string[])
      );
    } else {
      setSelectedFilterOptions(new Set());
    }
  }, [lanesTiers.selectedLanesTiers]);

  // Sync local state with Redux state for Status (only call setState when value changed to avoid infinite loop)
  useEffect(() => {
    const key = (status.selectedStatus as string[]).slice().sort().join(',');
    if (lastStatusRef.current === key) return;
    lastStatusRef.current = key;
    if (status.selectedStatus.length > 0) {
      setSelectedStatusOptions(new Set(status.selectedStatus as string[]));
    } else {
      setSelectedStatusOptions(new Set());
    }
  }, [status.selectedStatus]);

  useEffect(() => {
    const ids = momentTypeFilter.selectedMomentTypes || [];
    const key = ids.slice().sort().join(',');
    if (lastMomentTypeRef.current === key) return;
    lastMomentTypeRef.current = key;
    if (ids.length > 0) {
      setSelectedMomentTypeOptions(new Set(ids));
    } else {
      setSelectedMomentTypeOptions(new Set());
    }
  }, [momentTypeFilter.selectedMomentTypes]);

  // Default selection for Lanes/Tiers - only when both local and Redux are empty (avoids loop with sync effect)
  useEffect(() => {
    const reduxEmpty = lanesTiers.selectedLanesTiers.length === 0;
    const localEmpty =
      selectedFilterOptions instanceof Set && selectedFilterOptions.size === 0;
    if (!localEmpty || !reduxEmpty || lanesTiersFilterOptions.length === 0) {
      return;
    }
    const defaultSelection = new Set(
      lanesTiersFilterOptions
        .filter(option => {
          if (option.key === 'all') return false;
          const defaultKeys = [
            'events',
            'price-changes-and-offers',
            'l1',
            'l2',
            'l3'
          ];
          return defaultKeys.includes(option.key);
        })
        .map(option => option.key)
    );
    if (defaultSelection.size > 0) {
      lastLanesTiersRef.current = Array.from(defaultSelection).sort().join(',');
      setSelectedFilterOptions(defaultSelection);
      dispatch(actions.setSelectedLanesTiers(Array.from(defaultSelection)));
      saveUserPreferences({ lanesTiers: Array.from(defaultSelection) });
    }
  }, [
    selectedFilterOptions,
    lanesTiers.selectedLanesTiers.length,
    lanesTiersFilterOptions,
    dispatch,
    actions
  ]);

  // Sync local state with Redux state for Product Groups (only call setState when value changed to avoid infinite loop)
  useEffect(() => {
    const key = (productGroups.selectedGroups as string[])
      .slice()
      .sort()
      .join(',');
    if (lastProductGroupsRef.current === key) return;
    lastProductGroupsRef.current = key;
    if (productGroups.selectedGroups.length > 0) {
      setSelectedProductGroups(
        new Set(productGroups.selectedGroups as string[])
      );
    } else {
      setSelectedProductGroups(new Set());
    }
  }, [productGroups.selectedGroups]);

  // Sync local state with Redux state for Audiences (only call setState when value changed to avoid infinite loop)
  useEffect(() => {
    const key = (audiences.selectedAudiences as string[])
      .slice()
      .sort()
      .join(',');
    if (lastAudiencesRef.current === key) return;
    lastAudiencesRef.current = key;
    if (audiences.selectedAudiences.length > 0) {
      setSelectedAudiences(new Set(audiences.selectedAudiences as string[]));
    } else {
      setSelectedAudiences(new Set());
    }
  }, [audiences.selectedAudiences]);

  // Sync local state with Redux state for Features
  useEffect(() => {
    if (features.selectedFeatures.length > 0) {
      const reduxSelection = new Set(features.selectedFeatures as string[]);
      setSelectedFeaturesValues(reduxSelection);
    } else {
      // Clear local state when Redux state is cleared
      setSelectedFeaturesValues(new Set());
    }
  }, [features.selectedFeatures]);

  // Sync local state with Redux state for Gen AI filters
  useEffect(() => {
    if (genAiFilter.selectedFilters.length > 0) {
      const reduxSelection = new Set(genAiFilter.selectedFilters as string[]);
      setSelectedGenAiFilters(reduxSelection);
    } else {
      // Clear local state when Redux state is cleared
      setSelectedGenAiFilters(new Set());
    }
  }, [genAiFilter.selectedFilters]);

  // Sync local state with Redux state for Revenue Impact filters
  useEffect(() => {
    if (revenueImpactFilter.selectedFilters.length > 0) {
      const reduxSelection = new Set(
        revenueImpactFilter.selectedFilters as string[]
      );
      setSelectedRevenueImpactFilters(reduxSelection);
    } else {
      // Clear local state when Redux state is cleared
      setSelectedRevenueImpactFilters(new Set());
    }
  }, [revenueImpactFilter.selectedFilters]);

  // Re-sync all local filter UI when a saved view is applied from the sidebar.
  useEffect(() => {
    if (savedViewApplyToken === 0) {
      return;
    }

    lastAudiencesRef.current = '';
    lastProductGroupsRef.current = '';
    lastStatusRef.current = '';
    lastMomentTypeRef.current = '';
    lastLanesTiersRef.current = '';

    setSelectedAudiences(new Set(audiences.selectedAudiences as string[]));
    setSelectedProductGroups(new Set(productGroups.selectedGroups as string[]));
    setSelectedFeaturesValues(new Set(features.selectedFeatures as string[]));
    setSelectedGenAiFilters(new Set(genAiFilter.selectedFilters as string[]));
    setSelectedRevenueImpactFilters(
      new Set(revenueImpactFilter.selectedFilters as string[])
    );
    setSelectedStatusOptions(new Set(status.selectedStatus as string[]));
    setSelectedMomentTypeOptions(
      new Set(momentTypeFilter.selectedMomentTypes as string[])
    );
    setSelectedFilterOptions(
      new Set(lanesTiers.selectedLanesTiers as string[])
    );

    if (selectedEvents.selectedEvents?.length > 0 && eventOptions.length > 0) {
      const labels = eventOptions
        .filter(event => selectedEvents.selectedEvents.includes(event.value))
        .map(event => event.title);
      setSelectedeventsLabel(new Set(labels));
      setEventValue(selectedEvents.selectedEvents);
    } else {
      setSelectedeventsLabel(new Set());
      setEventValue([]);
    }
  }, [
    savedViewApplyToken,
    audiences.selectedAudiences,
    productGroups.selectedGroups,
    features.selectedFeatures,
    genAiFilter.selectedFilters,
    revenueImpactFilter.selectedFilters,
    status.selectedStatus,
    momentTypeFilter.selectedMomentTypes,
    lanesTiers.selectedLanesTiers,
    selectedEvents.selectedEvents,
    eventOptions
  ]);

  // STEP 1: Load saved preferences from localStorage and restore Redux state
  // This runs once when component mounts and user is authenticated
  useEffect(() => {
    if (restorationAttempted) {
      return;
    }

    setRestorationAttempted(true);

    // This restores shared Roadmap/My Stuff preferences — meaningless (and
    // risky) while mounted on Milestones, since NewFilter is shared between
    // both pages via RoadmapHeader. Don't consume the one-shot flag here so
    // a later Roadmap-route mount still gets to restore.
    if (!isRoadmapRoute) {
      return;
    }

    if (hasRestoredFilterPreferences) {
      return;
    }
    hasRestoredFilterPreferences = true;

    if (shouldPreferUrlOverLocalPreferences()) {
      return;
    }

    const preferences = getUserPreferences();

    // Restore full filter snapshot from localStorage (including empty arrays).
    dispatch(actions.setSelectedGroups(preferences.productGroups ?? []));
    dispatch(actions.setSelectedAudiences(preferences.audiences ?? []));
    dispatch(actions.setSelectedCats(preferences.categories ?? []));
    dispatch(actions.setSelectedFeatures(preferences.features ?? []));
    dispatch(actions.setSelectedProducts(preferences.products ?? []));
    dispatch(actions.setSelectedEvents(preferences.events ?? []));
    setEventValue(preferences.events ?? []);
    dispatch(actions.setGenAiFilters(preferences.genAiFilters ?? []));
    dispatch(
      actions.setCategoryProductSelection({
        categories: preferences.categoryProductCategories ?? [],
        products: preferences.categoryProductProducts ?? []
      })
    );
    dispatch(actions.setSelectedStatus(preferences.status ?? []));
    dispatch(
      actions.setHideCompletedItems(preferences.hideCompletedItems ?? false)
    );
    dispatch(
      actions.setRevenueImpactFilters(preferences.revenueImpactFilters ?? [])
    );
    dispatch(
      actions.setSelectedMomentTypes(preferences.momentTypeFilters ?? [])
    );
    dispatch(actions.setSelectedLanesTiers(preferences.lanesTiers ?? []));
    restoreMustWinState(preferences);

    // Note: STEP 2 will load ALL options first, then STEP 2B will update valid options with restored selections
  }, [isAuthenticated, isRoadmapRoute]); // Depend on authentication to ensure API calls work

  // STEP 2 completion tracking: Mark when restoration system is ready
  useEffect(() => {
    // Mark restoration complete when we have authenticated and attempted restoration
    if (
      isAuthenticated &&
      restorationAttempted &&
      !isInitialRestorationComplete
    ) {
      // Small delay to ensure restoration has had time to complete
      const timer = setTimeout(() => {
        setIsInitialRestorationComplete(true);
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, restorationAttempted, isInitialRestorationComplete]);

  // Extract events from release data
  useEffect(() => {
    if (releases?.releaseScheduled) {
      const eventsData = releases.releaseScheduled.find(release =>
        release.type.includes('Events')
      );

      if (eventsData?.projects) {
        // Flatten all projects from all quarters
        const allProjects = eventsData.projects.reduce((acc, quarterData) => {
          if (quarterData.project && Array.isArray(quarterData.project)) {
            return [...acc, ...quarterData.project];
          }
          return acc;
        }, []);

        // Map through all projects to get event names with counts of related issues
        const events_ = allProjects
          .map(project => {
            // Count related issues using badgecount from relationship object
            const relatedIssuesCount = project.relationship?.badgecount
              ? parseInt(project.relationship.badgecount, 10)
              : 0;

            return {
              label: `${project.title} (${relatedIssuesCount})`,
              value: project.issueId,
              title: project.title,
              relationships: project.relationships || [],
              relationship: project.relationship, // Add relationship object for future use
              quarter: project.dates?.targetQuarter || ''
            };
          })
          .filter(event => event.title && event.value); // Filter out any undefined/empty titles or values

        // Sort events alphabetically by title
        const sortedEvents = events_.sort((a, b) =>
          a.title.localeCompare(b.title)
        );

        // Only update if we have new events or if eventOptions is empty
        if (
          sortedEvents.length > 0 &&
          (eventOptions.length === 0 ||
            JSON.stringify(sortedEvents) !== JSON.stringify(eventOptions))
        ) {
          setEventOptions(sortedEvents);
        }
      } else {
        // If no events data, reset to empty array
        setEventOptions([]);
      }
    }
  }, [releases]);

  // Listen for changes in selectedEvents from Redux
  useEffect(() => {
    if (
      eventOptions.length > 0 &&
      selectedEvents.selectedEvents &&
      selectedEvents.selectedEvents.length > 0
    ) {
      let selectedValues = eventOptions
        .filter(group =>
          Array.from(selectedEvents.selectedEvents).includes(group.value)
        )
        .map(group => group.title);
      setSelectedeventsLabel(new Set(selectedValues));
    } else if (
      selectedEvents.selectedEvents &&
      selectedEvents.selectedEvents.length === 0
    ) {
      // Clear local state when Redux state is cleared
      setSelectedeventsLabel(new Set());
    }
  }, [selectedEvents.selectedEvents, eventOptions]);

  // STEP 3: Comprehensive UI restoration using ALL options (enabled/disabled) + localStorage preferences
  useEffect(() => {
    if (!restorationAttempted) return;

    const restoreFilterSelections = () => {
      // Use cached hierarchy data as primary source, fallback to individual options
      const availableOptions = {
        audiences:
          hierarchyFilters.customerAudiences.length > 0
            ? hierarchyFilters.customerAudiences
            : audienceOptions
                .map(opt => opt.value)
                .filter(val => val !== 'all'),
        productGroups:
          hierarchyFilters.productGroups.length > 0
            ? hierarchyFilters.productGroups
            : groupOptions.map(opt => opt.value).filter(val => val !== 'all'),
        features: featureOptions
          .map(opt => opt.value)
          .filter(val => val !== 'all'),
        events: eventOptions.map(opt => opt.title).filter(val => val !== 'All')
      };

      // Restore Audiences (NO DEPENDENCIES)
      if (
        availableOptions.audiences.length > 0 &&
        audiences.selectedAudiences?.length > 0
      ) {
        const validAudiences = audiences.selectedAudiences.filter(audience =>
          availableOptions.audiences.includes(audience)
        );

        if (validAudiences.length > 0) {
          const shouldIncludeAll =
            validAudiences.length === availableOptions.audiences.length;
          const finalSelection = shouldIncludeAll
            ? new Set([...validAudiences, 'all'])
            : new Set(validAudiences);
          setSelectedAudiences(finalSelection);
        }
      }

      // Restore Product Groups (NO DEPENDENCIES)
      if (
        availableOptions.productGroups.length > 0 &&
        productGroups.selectedGroups?.length > 0
      ) {
        const validGroups = productGroups.selectedGroups.filter(group =>
          availableOptions.productGroups.includes(group)
        );

        if (validGroups.length > 0) {
          const shouldIncludeAll =
            validGroups.length === availableOptions.productGroups.length;
          const finalSelection = shouldIncludeAll
            ? new Set([...validGroups, 'all'])
            : new Set(validGroups);
          setSelectedProductGroups(finalSelection);
        }
      }

      // Restore Features (NO DEPENDENCIES)
      if (
        availableOptions.features.length > 0 &&
        features.selectedFeatures?.length > 0
      ) {
        const validFeatures = features.selectedFeatures.filter(feature =>
          availableOptions.features.includes(feature)
        );

        if (validFeatures.length > 0) {
          const shouldIncludeAll =
            validFeatures.length === availableOptions.features.length;
          const finalSelection = shouldIncludeAll
            ? new Set([...validFeatures, 'all'])
            : new Set(validFeatures);
          setSelectedFeaturesValues(finalSelection);
        }
      }

      // Restore Events (NO DEPENDENCIES)
      if (
        availableOptions.events.length > 0 &&
        selectedEvents.selectedEvents?.length > 0
      ) {
        const validEventValues = selectedEvents.selectedEvents;
        const validEventLabels = eventOptions
          .filter(event => validEventValues.includes(event.value))
          .map(event => event.title);

        if (validEventLabels.length > 0) {
          const shouldIncludeAll =
            validEventLabels.length === availableOptions.events.length;
          const finalSelection = shouldIncludeAll
            ? new Set([...validEventLabels, 'All'])
            : new Set(validEventLabels);
          setSelectedeventsLabel(finalSelection);
        }
      }
    };

    // Trigger restoration when any options become available
    // Prioritize cached hierarchy data, fallback to individual API data
    const hasAnyOptions =
      hierarchyFilters.customerAudiences.length > 0 ||
      hierarchyFilters.productGroups.length > 0 ||
      hierarchyFilters.categories.length > 0 ||
      hierarchyFilters.products.length > 0 ||
      audienceOptions.length > 0 ||
      groupOptions.length > 0 ||
      featureOptions.length > 0 ||
      eventOptions.length > 0;

    if (hasAnyOptions) {
      restoreFilterSelections();
    }
  }, [
    // Trigger on hierarchy data (primary source)
    hierarchyFilters.customerAudiences,
    hierarchyFilters.productGroups,
    hierarchyFilters.categories,
    hierarchyFilters.products,
    // Fallback to individual option arrays
    audienceOptions,
    groupOptions,
    featureOptions,
    eventOptions,
    // Redux state with saved selections
    audiences.selectedAudiences,
    productGroups.selectedGroups,
    categories.selectedCats,
    productFilter.selectedProducts,
    features.selectedFeatures,
    selectedEvents.selectedEvents,
    // Only run after initial restoration attempt
    restorationAttempted
  ]);

  // STEP 2: Load hierarchy filters API - first ALL options, then valid options for restored selections
  useEffect(() => {
    if (
      isAuthenticated &&
      !hierarchyFilters.loading &&
      !hierarchyFilters.cached
    ) {
      // First: Load ALL available options with empty call
      dispatch(actions.loadHierarchyFilters({}));
    } else if (hierarchyFilters.cached) {
    }

    // NOTE: Features API call disabled - not needed on login
    // if (isAuthenticated && features.data.length === 0 && !features.loading) {
    //   dispatch(actions.loadFeatures({}));
    // }
  }, [isAuthenticated, hierarchyFilters.cached, hierarchyFilters.loading]);

  // STEP 2B: After getting all options, update valid options based on restored selections
  useEffect(() => {
    if (
      isAuthenticated &&
      hierarchyFilters.cached &&
      restorationAttempted &&
      hierarchyFilters.customerAudiences.length > 0 &&
      !validOptionsUpdated
    ) {
      // Ensure we have the full data and haven't done this yet

      const preferences = getUserPreferences();
      const hasRestoredSelections =
        preferences.productGroups.length > 0 ||
        preferences.audiences?.length > 0 ||
        preferences.categories.length > 0 ||
        preferences.products?.length > 0;

      if (hasRestoredSelections) {
        const restoredSelections = {
          productGroups: preferences.productGroups || [],
          customerAudiences: preferences.audiences || [],
          categories: preferences.categories || [],
          products: preferences.products || []
        };
        // This will call updateValidOptions, not hierarchyFiltersLoaded
        // dispatch(actions.loadHierarchyFilters(restoredSelections));
        setValidOptionsUpdated(true); // Mark as completed to prevent re-runs
      } else {
        // No restored selections, mark as updated anyway
        setValidOptionsUpdated(true);
      }
    }
  }, [
    isAuthenticated,
    hierarchyFilters.cached,
    hierarchyFilters.customerAudiences.length,
    restorationAttempted,
    validOptionsUpdated
  ]);

  useEffect(() => {
    const groups = hierarchyFilters.productGroups.map(item => ({
      label: item,
      value: item,
      // Disable only if other filters have selections AND this option is not valid
      disabled:
        (audiences.selectedAudiences?.length > 0 ||
          categories.selectedCats?.length > 0 ||
          productFilter.selectedProducts?.length > 0) &&
        !hierarchyFilters.validOptions.productGroups.includes(item)
    }));
    setGroupOptions(groups);
  }, [
    hierarchyFilters.productGroups,
    hierarchyFilters.validOptions.productGroups,
    audiences.selectedAudiences,
    categories.selectedCats,
    productFilter.selectedProducts
  ]);

  useEffect(() => {
    const audiencesList = hierarchyFilters.customerAudiences.map(item => ({
      label: item,
      value: item,
      // Disable only if other filters have selections AND this option is not valid
      disabled:
        (productGroups.selectedGroups?.length > 0 ||
          categories.selectedCats?.length > 0 ||
          productFilter.selectedProducts?.length > 0) &&
        !hierarchyFilters.validOptions.customerAudiences.includes(item)
    }));
    setAudienceOptions(audiencesList);
  }, [
    hierarchyFilters.customerAudiences,
    hierarchyFilters.validOptions.customerAudiences,
    productGroups.selectedGroups,
    categories.selectedCats,
    productFilter.selectedProducts
  ]);

  useEffect(() => {
    const featuresWithAll = hierarchyFilters.surfaces.map(item => ({
      label: item,
      value: item,
      // Disable only if other filters have selections AND this option is not valid
      disabled:
        (productGroups.selectedGroups?.length > 0 ||
          audiences.selectedAudiences?.length > 0 ||
          categories.selectedCats?.length > 0 ||
          productFilter.selectedProducts?.length > 0) &&
        !hierarchyFilters.validOptions.surfaces.includes(item)
    }));
    setFeatureOptions(featuresWithAll);
  }, [
    hierarchyFilters.surfaces,
    hierarchyFilters.validOptions.surfaces,
    productGroups.selectedGroups,
    audiences.selectedAudiences,
    categories.selectedCats,
    productFilter.selectedProducts
  ]);

  // OLD: Clear categories if no product groups are selected - Now using independent filters
  // useEffect(() => {
  //   // Clear categories if no product groups are selected
  //   if (Array.from(selectedProductGroups).filter(val => val !== 'all').length === 0) {
  //     setCategoryValue([]);
  //     return;
  //   }

  //   if (categoryOptions.length > 0) {
  //     const filteredCategoryValue = categoryValue.filter(value =>
  //       categories.data.includes(value) || value === 'all'
  //     );

  //     if (
  //       categoryValue.toLocaleString() !==
  //       filteredCategoryValue.toLocaleString()
  //     ) {
  //       // Always remove "all" first when data changes, then re-evaluate
  //       const selectedIndividualCategories = filteredCategoryValue.filter(value => value !== 'all');

  //       // Only check for "all" selection if we actually have individual items selected
  //       // and they exactly match all available options
  //       const individualCategories = categories.data;
  //       const allIndividualSelected = selectedIndividualCategories.length > 0 &&
  //         selectedIndividualCategories.length === individualCategories.length &&
  //         individualCategories.every(cat => selectedIndividualCategories.includes(cat));

  //       let finalCategoryValue;
  //       if (allIndividualSelected) {
  //         // Add "all" only if all individual categories are truly selected
  //         finalCategoryValue = [...selectedIndividualCategories, 'all'];
  //       } else {
  //         // No "all" if not all individual categories are selected
  //         finalCategoryValue = selectedIndividualCategories;
  //       }

  //       setCategoryValue(finalCategoryValue);

  //       // Only save to localStorage after restoration is complete
  //       if (isInitialRestorationComplete) {
  //         saveUserPreferences({ categories: selectedIndividualCategories });
  //       } else {
  //       }

  //       dispatch(actions.setSelectedCats(selectedIndividualCategories));
  //     }
  //   }
  // }, [categoryOptions, isInitialRestorationComplete]);

  // OLD: Clear products if no categories are selected - Now using independent filters
  // useEffect(() => {
  //   // Clear products if no categories are selected
  //   if (categoryValue.filter(val => val !== 'all').length === 0) {
  //     setSelectedProductsValues(new Set([]));
  //     return;
  //   }

  //   if (productOptions.length > 0) {
  //     const currentProductValues = Array.from(selectedProductsValues).map(String);
  //     const filteredProductValues = currentProductValues.filter(value =>
  //       productFilter.data.includes(value) || value === 'all'
  //     );

  //     if (
  //       currentProductValues.toString() !==
  //       filteredProductValues.toString()
  //     ) {
  //       // Always remove "all" first when data changes, then re-evaluate
  //       const selectedIndividualProducts = filteredProductValues.filter(value => value !== 'all');

  //       // Only check for "all" selection if we actually have individual items selected
  //       // and they exactly match all available options
  //       const individualProducts = productFilter.data;
  //       const allIndividualSelected = selectedIndividualProducts.length > 0 &&
  //         selectedIndividualProducts.length === individualProducts.length &&
  //         individualProducts.every(prod => selectedIndividualProducts.includes(prod));

  //       let finalProductSelection;
  //       if (allIndividualSelected) {
  //         // Add "all" only if all individual products are truly selected
  //         finalProductSelection = new Set([...selectedIndividualProducts, 'all']);
  //       } else {
  //         // No "all" if not all individual products are selected
  //         finalProductSelection = new Set(selectedIndividualProducts);
  //       }

  //       setSelectedProductsValues(finalProductSelection);

  //       // Only save to localStorage after restoration is complete
  //       if (isInitialRestorationComplete) {
  //         saveUserPreferences({ products: selectedIndividualProducts });
  //       } else {
  //       }

  //       dispatch(actions.setSelectedProducts(selectedIndividualProducts));
  //     }
  //   }
  // }, [productOptions, isInitialRestorationComplete]);

  // Products API should only be called after categories are selected
  // Removed initial API call on authentication

  // Centralized function to check and update "All" conditions for all filters
  const updateAllConditionsForAllFilters = () => {
    // Skip auto-correction if user just explicitly clicked "All" to prevent interference
    // Check both state and ref, and consider recent timestamps (within 1 second)
    const recentUserAction =
      userClickedAllRef.current &&
      Date.now() - userClickedAllRef.current.timestamp < 1000;

    if (userClickedAll || recentUserAction) {
      return;
    }
    // Check Audiences "All" condition
    if (audienceOptions.length > 0 && selectedAudiences instanceof Set) {
      const individualAudiences = audienceOptions.filter(
        option => option.value !== 'all'
      );
      const allSelectedValues = Array.from(selectedAudiences)
        .map(String)
        .filter(val => val !== 'all');

      // Filter selected values to only include those that exist in current options
      const validSelectedAudiences = allSelectedValues.filter(val =>
        individualAudiences.some(option => option.value === val)
      );

      const audiencesAllSelected =
        individualAudiences.length > 0 &&
        validSelectedAudiences.length === individualAudiences.length &&
        individualAudiences.every(option =>
          validSelectedAudiences.includes(option.value)
        );

      // Clean up invalid selections first (ONLY save when cleaning up invalid data)
      if (allSelectedValues.length !== validSelectedAudiences.length) {
        const cleanSelection = new Set(validSelectedAudiences);
        if (audiencesAllSelected) {
          cleanSelection.add('all');
        }
        setSelectedAudiences(cleanSelection);
        saveUserPreferences({ audiences: validSelectedAudiences });
        saveUserPreferences({ audienceLabels: Array.from(cleanSelection) });
      } else if (audiencesAllSelected && !selectedAudiences.has('all')) {
        // Just update UI state, don't save
        setSelectedAudiences(new Set([...validSelectedAudiences, 'all']));
      } else if (!audiencesAllSelected && selectedAudiences.has('all')) {
        // Just update UI state, don't save
        setSelectedAudiences(new Set(validSelectedAudiences));
      }
    }

    // Check Product Groups "All" condition
    if (groupOptions.length > 0 && selectedProductGroups instanceof Set) {
      const individualGroups = groupOptions.filter(
        option => option.value !== 'all'
      );
      const allSelectedValues = Array.from(selectedProductGroups)
        .map(String)
        .filter(val => val !== 'all');

      // Filter selected values to only include those that exist in current options
      const validSelectedGroups = allSelectedValues.filter(val =>
        individualGroups.some(option => option.value === val)
      );

      // Check if all available options are selected (among valid selections)
      const groupsAllSelected =
        individualGroups.length > 0 &&
        validSelectedGroups.length === individualGroups.length &&
        individualGroups.every(option =>
          validSelectedGroups.includes(option.value)
        );

      // If there are invalid selections, clean them up first (ONLY save when cleaning up invalid data)
      if (allSelectedValues.length !== validSelectedGroups.length) {
        const cleanSelection = new Set(validSelectedGroups);
        if (groupsAllSelected) {
          cleanSelection.add('all');
        }
        setSelectedProductGroups(cleanSelection);
        saveUserPreferences({ productGroups: validSelectedGroups });
        saveUserPreferences({ productGroupLabels: Array.from(cleanSelection) });
      } else if (groupsAllSelected && !selectedProductGroups.has('all')) {
        // Just update UI state, don't save
        setSelectedProductGroups(new Set([...validSelectedGroups, 'all']));
      } else if (!groupsAllSelected && selectedProductGroups.has('all')) {
        // Just update UI state, don't save
        setSelectedProductGroups(new Set(validSelectedGroups));
      }
    }

    // Check Events "All" condition
    if (eventOptions.length > 0 && selectedeventsLabel instanceof Set) {
      const individualEvents = eventOptions.filter(
        option => option.title !== 'All'
      );
      const allSelectedValues = Array.from(selectedeventsLabel)
        .map(String)
        .filter(val => val !== 'All');

      // Filter selected values to only include those that exist in current options
      const validSelectedEvents = allSelectedValues.filter(val =>
        individualEvents.some(option => option.title === val)
      );

      const eventsAllSelected =
        individualEvents.length > 0 &&
        validSelectedEvents.length === individualEvents.length &&
        individualEvents.every(option =>
          validSelectedEvents.includes(option.title)
        );

      // Clean up invalid selections first (ONLY save when cleaning up invalid data)
      if (allSelectedValues.length !== validSelectedEvents.length) {
        const cleanSelection = new Set(validSelectedEvents);
        if (eventsAllSelected) {
          cleanSelection.add('All');
        }
        setSelectedeventsLabel(cleanSelection);
        saveUserPreferences({ eventsLabel: Array.from(cleanSelection) });
      } else if (eventsAllSelected && !selectedeventsLabel.has('All')) {
        // Just update UI state, don't save
        setSelectedeventsLabel(new Set([...validSelectedEvents, 'All']));
      } else if (!eventsAllSelected && selectedeventsLabel.has('All')) {
        // Just update UI state, don't save
        setSelectedeventsLabel(new Set(validSelectedEvents));
      }
    }

    // Check Features "All" condition
    if (featureOptions.length > 0 && selectedFeaturesValues instanceof Set) {
      const individualFeatures = featureOptions.filter(
        option => option.value !== 'all'
      );
      const allSelectedValues = Array.from(selectedFeaturesValues)
        .map(String)
        .filter(val => val !== 'all');

      // Filter selected values to only include those that exist in current options
      const validSelectedFeatures = allSelectedValues.filter(val =>
        individualFeatures.some(option => option.value === val)
      );

      const featuresAllSelected =
        individualFeatures.length > 0 &&
        validSelectedFeatures.length === individualFeatures.length &&
        individualFeatures.every(option =>
          validSelectedFeatures.includes(option.value)
        );

      // Clean up invalid selections first (ONLY save when cleaning up invalid data)
      if (allSelectedValues.length !== validSelectedFeatures.length) {
        const cleanSelection = new Set(validSelectedFeatures);
        if (featuresAllSelected) {
          cleanSelection.add('all');
        }
        setSelectedFeaturesValues(cleanSelection);
        saveUserPreferences({ features: validSelectedFeatures });
      } else if (featuresAllSelected && !selectedFeaturesValues.has('all')) {
        // Just update UI state, don't save
        setSelectedFeaturesValues(new Set([...validSelectedFeatures, 'all']));
      } else if (!featuresAllSelected && selectedFeaturesValues.has('all')) {
        // Just update UI state, don't save
        setSelectedFeaturesValues(new Set(validSelectedFeatures));
      }
    }
  };

  // OLD: Load products only when categories are selected - Now using hierarchy filters
  // useEffect(() => {
  //   if (categoryValue.length > 0) {
  //     const selectedValues = Array.from(selectedProductGroups).map(String);

  //     dispatch(actions.loadProductFilter({
  //         productGroups: selectedValues,
  //         categories: categoryValue
  //     }));
  //   } else {
  //     // Clear products when no categories are selected
  //     dispatch(actions.productFilterLoaded([]));
  //     // Also clear selected products
  //     setSelectedProductsValues(new Set([]));
  //     dispatch(actions.setSelectedProducts([]));
  //   }
  // }, [selectedProductGroups, categoryValue]);

  // Call centralized "All" check whenever options change for any filter
  // But only after initial localStorage restoration is complete
  useEffect(() => {
    if (isInitialRestorationComplete) {
      updateAllConditionsForAllFilters();
    } else {
    }
  }, [
    isInitialRestorationComplete,
    userClickedAll,
    audienceOptions,
    groupOptions,
    eventOptions,
    featureOptions,
    selectedAudiences,
    selectedProductGroups,
    selectedeventsLabel,
    selectedFeaturesValues
  ]);

  const handleGroupChange = (selections: Selection) => {
    // ✅ Clear Must Win dropdown special UI when regular filters are modified
    clearMustWinState();

    if (selections instanceof Set) {
      const currentSelection = new Set(
        Array.from(selections).map(s => String(s))
      );
      const previousSelection =
        selectedProductGroups instanceof Set
          ? selectedProductGroups
          : new Set();

      // Get all enabled (valid) individual options
      const enabledIndividualOptions = groupOptions.filter(
        option => option.value !== 'all' && !option.disabled
      );
      const enabledIndividualValues = enabledIndividualOptions.map(
        option => option.value
      );

      // Check if ALL valid options were selected in previous state (conceptually "All" was selected)
      const allValidOptionsWereSelected =
        previousSelection.has('all') ||
        (enabledIndividualValues.length > 0 &&
          enabledIndividualValues.every(val => previousSelection.has(val)));

      // Check current selection state
      const allValidOptionsAreSelected =
        currentSelection.has('all') ||
        (enabledIndividualValues.length > 0 &&
          enabledIndividualValues.every(val => currentSelection.has(val)));

      // Detect user actions
      const allJustSelected =
        !allValidOptionsWereSelected && allValidOptionsAreSelected;
      const allJustDeselected =
        allValidOptionsWereSelected && !allValidOptionsAreSelected;

      // Track explicit "All" button clicks to prevent auto-correction interference
      if (allJustSelected) {
        const action = { filter: 'productGroups', action: 'select' as const };
        setUserClickedAll(action);
        userClickedAllRef.current = { ...action, timestamp: Date.now() };
      } else if (allJustDeselected) {
        const action = { filter: 'productGroups', action: 'deselect' as const };
        setUserClickedAll(action);
        userClickedAllRef.current = { ...action, timestamp: Date.now() };
      }

      // Calculate final selection FIRST, then update state ONCE
      let finalUISelection: Selection;
      let reduxValues: string[];

      if (allJustSelected) {
        // For "All" selection, show only enabled options
        const enabledIndividualKeys = new Set(
          groupOptions
            .filter(option => option.value !== 'all' && !option.disabled)
            .map(option => option.value)
        );
        enabledIndividualKeys.add('all');
        finalUISelection = enabledIndividualKeys;
        reduxValues = Array.from(enabledIndividualKeys).filter(
          val => val !== 'all'
        );
      } else if (allJustDeselected) {
        // For "All" deselection, clear everything (regardless of what React Spectrum sent us)
        finalUISelection = new Set([]);
        reduxValues = [];
      } else {
        // For individual selections, calculate final selection
        const enabledIndividualOptions = groupOptions.filter(
          option => option.value !== 'all' && !option.disabled
        );
        const selectedIndividualValues = Array.from(currentSelection).filter(
          val => val !== 'all'
        );

        // Check if all enabled individual options are selected
        const allEnabledSelected =
          enabledIndividualOptions.length > 0 &&
          selectedIndividualValues.length === enabledIndividualOptions.length &&
          enabledIndividualOptions.every(option =>
            selectedIndividualValues.includes(option.value)
          );

        if (allEnabledSelected) {
          // If all enabled individual options are selected, add "all" to the selection
          finalUISelection = new Set([...Array.from(currentSelection), 'all']);
        } else {
          // Remove "all" from selection if not all enabled individual items are selected
          finalUISelection = new Set(selectedIndividualValues);
        }
        reduxValues = selectedIndividualValues;
      }

      // Update UI state IMMEDIATELY - ONLY ONE UPDATE
      setSelectedProductGroups(finalUISelection);

      // Update Redux and save preferences
      dispatch(actions.setSelectedGroups(reduxValues));
      saveUserPreferences({ productGroups: reduxValues });
      saveUserPreferences({
        productGroupLabels: Array.from(finalUISelection as Set<string>)
      });

      // Clear dependent data if all was deselected
      if (allJustDeselected) {
        dispatch(actions.catgoriesLoaded([]));
        dispatch(actions.setSelectedEvents([]));
        dispatch(actions.updateHighlightedIssue({ relatedIssues: [] }));
      }

      // Call hierarchy filters API with selected values to update valid options if not clearing
      if (reduxValues.length > 0 || allJustSelected || !allJustDeselected) {
        const currentSelections = {
          productGroups: reduxValues,
          customerAudiences: audiences.selectedAudiences,
          categories: categories.selectedCats,
          products: productFilter.selectedProducts
        };
        // dispatch(actions.loadHierarchyFilters(currentSelections));
      }

      // Set user interaction flag immediately and clear it after state updates settle
      if (allJustSelected || allJustDeselected) {
        setTimeout(() => {
          setUserClickedAll(null);
          userClickedAllRef.current = null;
        }, 1000); // Long timeout to ensure all state updates and auto-corrections complete
      }
    } else {
      setSelectedProductGroups(selections);
      dispatch(actions.setSelectedGroups([]));
      saveUserPreferences({ productGroups: [] });
      saveUserPreferences({ productGroupLabels: [] });
    }
  };

  const handleAudienceChange = (selections: Selection) => {
    // ✅ Clear Must Win dropdown special UI when regular filters are modified
    clearMustWinState();

    if (selections instanceof Set) {
      const currentSelection = new Set(
        Array.from(selections).map(s => String(s))
      );
      const previousSelection =
        selectedAudiences instanceof Set ? selectedAudiences : new Set();

      // Check if "all" was just clicked
      const allJustSelected =
        currentSelection.has('all') && !previousSelection.has('all');
      const allJustDeselected =
        !currentSelection.has('all') && previousSelection.has('all');

      // Track explicit "All" button clicks to prevent auto-correction interference
      if (allJustSelected) {
        setUserClickedAll({ filter: 'audiences', action: 'select' });
      } else if (allJustDeselected) {
        setUserClickedAll({ filter: 'audiences', action: 'deselect' });
      }

      // Calculate final selection FIRST, then update state ONCE
      let finalUISelection: Selection;
      let reduxValues: string[];

      if (allJustSelected) {
        // For "All" selection, show only enabled options
        const enabledIndividualKeys = new Set(
          audienceOptions
            .filter(option => option.value !== 'all' && !option.disabled)
            .map(option => option.value)
        );
        enabledIndividualKeys.add('all');
        finalUISelection = enabledIndividualKeys;
        reduxValues = Array.from(enabledIndividualKeys).filter(
          val => val !== 'all'
        );
      } else if (allJustDeselected) {
        // For "All" deselection, clear everything
        finalUISelection = new Set([]);
        reduxValues = [];
      } else {
        // For individual selections, calculate final selection
        const enabledIndividualOptions = audienceOptions.filter(
          option => option.value !== 'all' && !option.disabled
        );
        const selectedIndividualValues = Array.from(currentSelection).filter(
          val => val !== 'all'
        );

        // Check if all enabled individual options are selected
        const allEnabledSelected =
          enabledIndividualOptions.length > 0 &&
          selectedIndividualValues.length === enabledIndividualOptions.length &&
          enabledIndividualOptions.every(option =>
            selectedIndividualValues.includes(option.value)
          );

        if (allEnabledSelected) {
          // If all enabled individual options are selected, add "all" to the selection
          finalUISelection = new Set([...Array.from(currentSelection), 'all']);
        } else {
          // Remove "all" from selection if not all enabled individual items are selected
          finalUISelection = new Set(selectedIndividualValues);
        }
        reduxValues = selectedIndividualValues;
      }

      // Update UI state IMMEDIATELY - ONLY ONE UPDATE
      setSelectedAudiences(finalUISelection);

      // Update Redux and save preferences
      dispatch(actions.setSelectedAudiences(reduxValues));
      saveUserPreferences({ audiences: reduxValues });
      saveUserPreferences({
        audienceLabels: Array.from(finalUISelection as Set<string>)
      });

      // Call hierarchy filters API with selected values to update valid options if not clearing
      if (reduxValues.length > 0 || allJustSelected || !allJustDeselected) {
        const currentSelections = {
          productGroups: productGroups.selectedGroups,
          customerAudiences: reduxValues,
          categories: categories.selectedCats,
          products: productFilter.selectedProducts
        };
        // dispatch(actions.loadHierarchyFilters(currentSelections));
      }

      // Clear the user interaction flag after a short delay
      if (allJustSelected || allJustDeselected) {
        setTimeout(() => setUserClickedAll(null), 150);
      }
    } else {
      setSelectedAudiences(selections);
      dispatch(actions.setSelectedAudiences([]));
      saveUserPreferences({ audiences: [] });
      saveUserPreferences({ audienceLabels: [] });
    }
  };

  // OLD: Reload product groups when audiences change - Now using independent filters
  // const selectedAudiencesString = useMemo(() => {
  //   return audiences.selectedAudiences.join(',');
  // }, [audiences.selectedAudiences]);

  // useEffect(() => {
  //   if (isAuthenticated && selectedAudiencesString !== prevAudiencesRef.current) {
  //     prevAudiencesRef.current = selectedAudiencesString;
  //     dispatch(actions.loadProductGroups({ audiences: audiences.selectedAudiences }));
  //   }
  // }, [selectedAudiencesString, isAuthenticated, dispatch]);

  const handleEventChange = (selections: Selection) => {
    // ✅ Clear Must Win dropdown special UI when regular filters are modified
    clearMustWinState();

    // ⭐ CLEAR ALL FILTERS WHEN EVENT FILTER IS FIRST APPLIED
    // Check if we're going from "no events" to "some events"
    const hadNoEventsSelected = selectedEvents.selectedEvents.length === 0;

    // Declare variables at function scope for use in highlighting logic
    let currentSelection = new Set<string>();
    let allJustSelected = false;
    let allJustDeselected = false;

    if (selections instanceof Set) {
      currentSelection = new Set(Array.from(selections).map(s => String(s)));
      const previousSelection =
        selectedeventsLabel instanceof Set ? selectedeventsLabel : new Set();

      // Check if we're about to select events when we had none before
      const willHaveEventsSelected =
        currentSelection.size > 0 &&
        !currentSelection.has('All') &&
        Array.from(currentSelection).every(s => s !== '');

      // ⭐ CLEAR ALL OTHER FILTERS when going from "no events" to "some events"
      if (
        hadNoEventsSelected &&
        (willHaveEventsSelected || currentSelection.has('All'))
      ) {
        console.log('🧹 Event filter applied - Clearing all other filters');

        // Clear ALL filters
        dispatch(actions.clearGenAiFilters());
        dispatch(actions.setSelectedFeatures([]));
        dispatch(actions.setSelectedProducts([]));
        dispatch(actions.setSelectedCats([]));
        dispatch(actions.setSelectedGroups([]));
        dispatch(actions.setSelectedAudiences([]));
        dispatch(actions.clearCategoryProductSelection());
        dispatch(actions.clearRevenueImpactFilters()); // Clear Revenue Impact filters
        dispatch(actions.clearMomentTypeFilter());
        clearMustWinState(); // Clear Must Win dropdown special UI

        // Clear local states as well
        setSelectedGenAiFilters(new Set([]));
        setSelectedMomentTypeOptions(new Set([]));
        setSelectedFeaturesValues(new Set([]));
        setSelectedProductGroups(new Set([]));
        setSelectedAudiences(new Set([]));

        // Clear saved preferences for all filters
        saveUserPreferences({ genAiFilters: [] });
        saveUserPreferences({ features: [] });
        saveUserPreferences({ products: [] });
        saveUserPreferences({ categories: [] });
        saveUserPreferences({ productGroups: [] });
        saveUserPreferences({ audiences: [] });
        saveUserPreferences({ revenueImpactFilters: [] }); // Clear Revenue Impact filters from localStorage
        saveUserPreferences({ momentTypeFilters: [] });
      }

      // Check if "all" was just clicked
      allJustSelected =
        currentSelection.has('All') && !previousSelection.has('All');
      allJustDeselected =
        !currentSelection.has('All') && previousSelection.has('All');

      // Track explicit "All" button clicks to prevent auto-correction interference
      if (allJustSelected) {
        setUserClickedAll({ filter: 'events', action: 'select' });
      } else if (allJustDeselected) {
        setUserClickedAll({ filter: 'events', action: 'deselect' });
      }

      if (allJustSelected) {
        // If "all" was just selected, select all individual options (excluding "All")
        const allIndividualKeys = new Set(
          eventOptions
            .filter(option => option.title !== 'All')
            .map(option => option.title)
        );
        allIndividualKeys.add('All'); // Keep "All" selected for UI
        setSelectedeventsLabel(allIndividualKeys);

        // For Redux, send all individual values
        const selectedValues = eventOptions
          .filter(
            group =>
              Array.from(allIndividualKeys).includes(group.title) &&
              group.title !== 'All'
          )
          .flatMap(group => group.value);

        dispatch(actions.setSelectedEvents(selectedValues));
        setEventValue(selectedValues);
        saveUserPreferences({ events: selectedValues });
        saveUserPreferences({ eventsLabel: Array.from(allIndividualKeys) });
      } else if (allJustDeselected) {
        // If "all" was just deselected, clear all selections
        setSelectedeventsLabel(new Set([]));
        dispatch(actions.setSelectedEvents([]));
        setEventValue([]);
        saveUserPreferences({ events: [] });
        saveUserPreferences({ eventsLabel: [] });

        // CRITICAL FIX: When events filter is cleared, only nest if NO other filters require unnesting
        // Check if surfaces or Gen AI filters are still active
        const surfacesActive = features.selectedFeatures.length > 0;
        const genAiActive = genAiFilter.selectedFilters.length > 0;

        // Only clear unnest state if no other filters that require unnesting are active
        if (!surfacesActive && !genAiActive) {
          // ✅ Use new clearHighlightedIssues action to completely clear highlights
          dispatch(actions.clearHighlightedIssues());

          // Set to nested state (unNestAll = false)
          dispatch(actions.setUnNestAll(false));
        }
      } else {
        // Handle individual item selection/deselection normally
        const individualOptions = eventOptions.filter(
          option => option.title !== 'All'
        );
        const selectedIndividualTitles = Array.from(currentSelection).filter(
          val => val !== 'All'
        );

        // Check if all individual options are selected
        const allIndividualSelected =
          individualOptions.length > 0 &&
          selectedIndividualTitles.length === individualOptions.length &&
          individualOptions.every(option =>
            selectedIndividualTitles.includes(option.title)
          );

        let finalSelection = currentSelection;
        if (allIndividualSelected) {
          // If all individual options are selected, add "All" to the selection
          finalSelection = new Set([...Array.from(currentSelection), 'All']);
        } else {
          // Remove "All" from selection if not all individual items are selected
          finalSelection = new Set(selectedIndividualTitles);
        }

        const selectedValues = eventOptions
          .filter(group => selectedIndividualTitles.includes(group.title))
          .flatMap(group => group.value);

        setSelectedeventsLabel(finalSelection);
        dispatch(actions.setSelectedEvents(selectedValues));
        setEventValue(selectedValues);
        saveUserPreferences({ events: selectedValues });
        saveUserPreferences({ eventsLabel: Array.from(finalSelection) });

        // If no events are selected (all deselected individually), check other filters before nesting
        if (selectedValues.length === 0) {
          // Check if surfaces or Gen AI filters are still active
          const surfacesActive = features.selectedFeatures.length > 0;
          const genAiActive = genAiFilter.selectedFilters.length > 0;

          // Only clear unnest state if no other filters that require unnesting are active
          if (!surfacesActive && !genAiActive) {
            dispatch(
              actions.updateHighlightedIssue({
                relatedIssues: [],
                unnestParentIds: []
              })
            );
            // Set to nested state (unNestAll = false)
            dispatch(actions.setUnNestAll(false));
          }
        }
      }
    } else {
      setSelectedeventsLabel(selections);
      dispatch(actions.setSelectedEvents([]));
      setEventValue([]);
      saveUserPreferences({ events: [] });
      saveUserPreferences({ eventsLabel: [] });
    }

    // Get selected values for highlighting logic - use the correct values based on what was just selected
    let finalSelectedValues: string[];
    let selectedEventValues: string[];

    if (allJustSelected) {
      // Use the values we just set when "All" was selected
      finalSelectedValues = eventOptions
        .filter(option => option.title !== 'All')
        .map(option => option.title);
      selectedEventValues = eventOptions
        .filter(group => group.title !== 'All')
        .flatMap(group => group.value);
    } else if (allJustDeselected) {
      // No events selected when "All" is deselected
      finalSelectedValues = [];
      selectedEventValues = [];
    } else {
      // Use current selection for individual selections
      const currentSelectedTitles = Array.from(currentSelection).filter(
        (v): v is string => v !== 'All'
      );
      finalSelectedValues = currentSelectedTitles;
      selectedEventValues = eventOptions
        .filter(group => currentSelectedTitles.includes(group.title))
        .flatMap(group => group.value);
    }

    if (selectedEventValues.length > 0) {
      // Get all related issue IDs for selected events
      const selectedEvents = eventOptions.filter(event =>
        selectedEventValues.includes(event.value)
      );

      const relatedIssues = new Set<string>();
      const parentToUnnest = new Set<string>();
      const highlightSet = new Set<string>();

      selectedEvents.forEach(event => {
        // Add the event's own ID
        highlightSet.add(event.value);

        // Use releaseHierarchyPaths from relationship object
        if (
          event.relationship?.releaseHierarchyPaths &&
          event.relationship.releaseHierarchyPaths.length > 0
        ) {
          // Extract issue IDs from releaseHierarchyPaths
          event.relationship.releaseHierarchyPaths.forEach(path => {
            if (path.issue) {
              // Prefer phaseId (UUID) over phase name for more accurate matching
              if (path.phaseId) {
                // Create composite key with UUID: issueId-phaseId-showOnTimeline
                const compositeKey = `${path.issue}-${path.phaseId}-false`;
                highlightSet.add(compositeKey);
              } else if (path.phase) {
                // Fallback to phase name if phaseId not available
                const compositeKey = `${path.issue}-${path.phase}-false`;
                highlightSet.add(compositeKey);
              } else {
                // No phase specified, highlight all phases/instances of this issue
                highlightSet.add(path.issue);
              }

              // If this is a child, track parent for unnesting (but don't highlight the parent)
              if (path.parent && path.parent !== event.value) {
                parentToUnnest.add(path.parent);
                // Don't add parent to highlightSet - parents should only be unnested, not highlighted
              }
            }
          });
        }
      });

      // Ensure we have at least the selected events highlighted
      if (highlightSet.size === 0) {
        selectedEvents.forEach(event => {
          highlightSet.add(event.value);
        });
      }

      dispatch(
        actions.updateHighlightedIssue({
          relatedIssues: Array.from(highlightSet)
        })
      );

      // Check if any highlighted items match items in the TBD section
      // If yes, open the parking lot to show them
      const tbdData = homeState.releaseData?.tbd || [];

      const hasMatchInTBD = tbdData.some((item: any) =>
        item.project?.some((project: any) => {
          if (!project.issueId) return false;

          // Check for direct match (issueId only)
          if (highlightSet.has(project.issueId)) return true;

          // Check for composite key match with releasePhaseId (UUID)
          if (project.releasePhaseId) {
            const compositeKey = `${project.issueId}-${project.releasePhaseId}-false`;
            if (highlightSet.has(compositeKey)) return true;
          }

          // Check for composite key match with releasePhase (string name)
          if (project.releasePhase) {
            const compositeKey = `${project.issueId}-${project.releasePhase}-false`;
            if (highlightSet.has(compositeKey)) return true;
          }

          return false;
        })
      );

      // If there are matches in TBD and parking lot is hidden, open it
      if (hasMatchInTBD) {
        dispatch(actions.setParkingLot(true));
      }
    } else {
      // Clear highlights when no events are selected
      dispatch(actions.updateHighlightedIssue({ relatedIssues: [] }));
    }
  };

  const handleFeatureChange = (selections: Selection) => {
    // ✅ Clear Must Win dropdown special UI when regular filters are modified
    clearMustWinState();

    if (selections instanceof Set) {
      const currentSelection = new Set(
        Array.from(selections).map(s => String(s))
      );
      const previousSelection =
        selectedFeaturesValues instanceof Set
          ? selectedFeaturesValues
          : new Set();

      // Check if "all" was just clicked
      const allJustSelected =
        currentSelection.has('all') && !previousSelection.has('all');
      const allJustDeselected =
        !currentSelection.has('all') && previousSelection.has('all');

      // Track explicit "All" button clicks to prevent auto-correction interference
      if (allJustSelected) {
        setUserClickedAll({ filter: 'features', action: 'select' });
      } else if (allJustDeselected) {
        setUserClickedAll({ filter: 'features', action: 'deselect' });
      }

      if (allJustSelected) {
        // If "all" was just selected, select all individual options (excluding "all")
        const allIndividualKeys = new Set(
          featureOptions
            .filter(option => option.value !== 'all')
            .map(option => option.value)
        );
        allIndividualKeys.add('all'); // Keep "all" selected for UI
        setSelectedFeaturesValues(allIndividualKeys);

        // For Redux, send all individual values
        const featureValues = Array.from(allIndividualKeys).filter(
          val => val !== 'all'
        );
        dispatch(actions.setSelectedFeatures(featureValues));
        saveUserPreferences({ features: featureValues });

        // ✅ When "Select All" is clicked, set unnest to true
        dispatch(actions.setUnNestAll(true));
      } else if (allJustDeselected) {
        // If "all" was just deselected, clear all selections
        setSelectedFeaturesValues(new Set([]));
        dispatch(actions.setSelectedFeatures([]));
        saveUserPreferences({ features: [] });

        // CRITICAL FIX: When surface filter is cleared, set to nested state (unNestAll = false)
        // Surface filter requires unnesting to show features, so clearing it should return to nested view
        dispatch(
          actions.updateHighlightedIssue({
            relatedIssues: [],
            unnestParentIds: []
          })
        );

        // Set to nested state (unNestAll = false) instead of restoring previous state
        dispatch(actions.setUnNestAll(false));
        dispatch(actions.clearPreviousUnNestAllState());

        // ✅ CRITICAL: Clear isUnNested preference from localStorage
        // This allows Roadmap.tsx useEffect to properly re-nest when filters are cleared
        removeUserPreferenceField('isUnNested');
      } else {
        // Handle individual item selection/deselection normally
        const individualOptions = featureOptions.filter(
          option => option.value !== 'all'
        );
        const selectedIndividualValues = Array.from(currentSelection).filter(
          val => val !== 'all'
        );

        // Check if all individual options are selected
        const allIndividualSelected =
          individualOptions.length > 0 &&
          selectedIndividualValues.length === individualOptions.length &&
          individualOptions.every(option =>
            selectedIndividualValues.includes(option.value)
          );

        let finalSelection = currentSelection;
        if (allIndividualSelected) {
          // If all individual options are selected, add "all" to the selection
          finalSelection = new Set([...Array.from(currentSelection), 'all']);
        } else {
          // Remove "all" from selection if not all individual items are selected
          finalSelection = new Set(selectedIndividualValues);
        }

        setSelectedFeaturesValues(finalSelection);
        dispatch(actions.setSelectedFeatures(selectedIndividualValues));
        saveUserPreferences({ features: selectedIndividualValues });

        // ✅ When surface filters are selected, set unnest to true
        if (selectedIndividualValues.length > 0) {
          dispatch(actions.setUnNestAll(true));
        }

        // ✅ If no features are selected (all deselected individually), clear highlights and re-nest
        if (selectedIndividualValues.length === 0) {
          // Clear ALL highlights when all surfaces are deselected
          dispatch(actions.clearHighlightedIssues());

          // CRITICAL FIX: When surface filter is cleared, set to nested state (unNestAll = false)
          // Surface filter requires unnesting to show features, so clearing it should return to nested view
          dispatch(actions.setUnNestAll(false));
          dispatch(actions.clearPreviousUnNestAllState());

          // ✅ CRITICAL: Clear isUnNested preference from localStorage
          // This allows Roadmap.tsx useEffect to properly re-nest when filters are cleared
          removeUserPreferenceField('isUnNested');
        } else {
          // ✅ Some features still selected - recalculate highlights based on remaining selections
          if (releases?.releaseScheduled) {
            const highlightSet = new Set<string>();
            const parentToUnnest = new Set<string>();

            // Iterate through all releases to find matching features
            releases.releaseScheduled.forEach(releaseType => {
              if (releaseType.projects) {
                releaseType.projects.forEach(quarterData => {
                  if (
                    quarterData.project &&
                    Array.isArray(quarterData.project)
                  ) {
                    quarterData.project.forEach(moment => {
                      // Check if moment has features
                      if (moment.features && Array.isArray(moment.features)) {
                        // Filter features based on selected surface filters
                        const matchingFeatures = moment.features.filter(
                          feature => {
                            const surface =
                              feature.surface || feature.webPlatform;
                            return (
                              surface &&
                              selectedIndividualValues.includes(surface)
                            );
                          }
                        );

                        // If moment has matching features, highlight it and unnest
                        if (matchingFeatures.length > 0) {
                          // Highlight the parent moment
                          highlightSet.add(moment.issueId);

                          // Highlight each individual matching feature
                          matchingFeatures.forEach(feature => {
                            const featureUniqueId =
                              feature.featureId || feature.issueId;
                            if (featureUniqueId) {
                              highlightSet.add(featureUniqueId);
                            }
                          });

                          // Mark this moment's parent for unnesting
                          parentToUnnest.add(moment.issueId);
                        }
                      }
                    });
                  }
                });
              }
            });

            // Save current unnest state before forcing unnest (if not already saved)
            if (parentToUnnest.size > 0) {
              dispatch(actions.savePreviousUnNestAllState());
              dispatch(actions.setUnNestAll(true));
            }

            // ✅ Clear first, then update - prevents merging with old highlights
            dispatch(actions.clearHighlightedIssues());
            // Apply highlighting to matching moments AND features
            dispatch(
              actions.updateHighlightedIssue({
                relatedIssues: Array.from(highlightSet)
              })
            );
          }
        }
      }

      // Clear the user interaction flag after a short delay
      if (allJustSelected || allJustDeselected) {
        setTimeout(() => setUserClickedAll(null), 150);
      }
    } else {
      setSelectedFeaturesValues(selections);
      dispatch(actions.setSelectedFeatures([]));
      saveUserPreferences({ features: [] });

      // CRITICAL FIX: When surface filter is cleared, set to nested state (unNestAll = false)
      // Surface filter requires unnesting to show features, so clearing it should return to nested view
      dispatch(
        actions.updateHighlightedIssue({
          relatedIssues: [],
          unnestParentIds: []
        })
      );

      // Set to nested state (unNestAll = false) instead of restoring previous state
      dispatch(actions.setUnNestAll(false));
      dispatch(actions.clearPreviousUnNestAllState());

      // ✅ CRITICAL: Clear isUnNested preference from localStorage
      // This allows Roadmap.tsx useEffect to properly re-nest when filters are cleared
      removeUserPreferenceField('isUnNested');
    }
  };

  // Handler for Gen AI filter changes
  const handleGenAiFilterChange = (selections: Selection) => {
    // ✅ Clear Must Win dropdown special UI when regular filters are modified
    clearMustWinState();

    if (selections instanceof Set) {
      const currentSelection = new Set(
        Array.from(selections).map(s => String(s))
      );
      const selectedFilters = Array.from(currentSelection);

      setSelectedGenAiFilters(currentSelection);
      dispatch(actions.setGenAiFilters(selectedFilters));
      saveUserPreferences({ genAiFilters: selectedFilters });

      // ✅ Always recalculate highlights based on current filter selection
      if (selectedFilters.length > 0) {
        // Get all release data to find features
        if (releases?.releaseScheduled) {
          const highlightSet = new Set<string>();
          const parentToUnnest = new Set<string>();

          // Iterate through all releases
          releases.releaseScheduled.forEach(releaseType => {
            if (releaseType.projects) {
              releaseType.projects.forEach(quarterData => {
                if (quarterData.project && Array.isArray(quarterData.project)) {
                  quarterData.project.forEach(moment => {
                    // Check if moment has features
                    if (moment.features && Array.isArray(moment.features)) {
                      // Filter features based on selected filters
                      const matchingFeatures = moment.features.filter(
                        feature => {
                          if (selectedFilters.includes('gen-ai-all-features')) {
                            return feature.genai === true;
                          } else if (
                            selectedFilters.includes('gen-ai-consumes-credits')
                          ) {
                            return (
                              feature.genai === true &&
                              feature.consumesCredit === true
                            );
                          }
                          return false;
                        }
                      );

                      // If moment has matching features, highlight it and unnest
                      if (matchingFeatures.length > 0) {
                        // Highlight the parent moment
                        highlightSet.add(moment.issueId);

                        // Highlight each individual matching feature
                        matchingFeatures.forEach(feature => {
                          const featureUniqueId =
                            feature.featureId || feature.issueId;
                          if (featureUniqueId) {
                            highlightSet.add(featureUniqueId);
                          }
                        });

                        // Mark this moment's parent for unnesting
                        parentToUnnest.add(moment.issueId);
                      }
                    }
                  });
                }
              });
            }
          });

          // Save current unnest state before forcing unnest (if not already saved)
          if (parentToUnnest.size > 0) {
            dispatch(actions.savePreviousUnNestAllState());
            dispatch(actions.setUnNestAll(true));
          }

          // ✅ Clear first, then update - prevents merging with old highlights from other filters
          dispatch(actions.clearHighlightedIssues());
          // Apply highlighting to matching moments AND features
          dispatch(
            actions.updateHighlightedIssue({
              relatedIssues: Array.from(highlightSet)
            })
          );
        }
      } else {
        // ✅ Clear ALL highlights when no GenAI filters are selected
        dispatch(actions.clearHighlightedIssues());

        // CRITICAL FIX: When Gen AI filter is cleared, set to nested state (unNestAll = false)
        // Gen AI filter requires unnesting to show features, so clearing it should return to nested view
        dispatch(actions.setUnNestAll(false));
        dispatch(actions.clearPreviousUnNestAllState());

        // ✅ CRITICAL: Clear isUnNested preference from localStorage
        // This allows Roadmap.tsx useEffect to properly re-nest when filters are cleared
        removeUserPreferenceField('isUnNested');
      }
    } else {
      setSelectedGenAiFilters(selections);
      dispatch(actions.setGenAiFilters([]));
      saveUserPreferences({ genAiFilters: [] });
      // ✅ Clear ALL highlights
      dispatch(actions.clearHighlightedIssues());

      // CRITICAL FIX: When Gen AI filter is cleared, set to nested state (unNestAll = false)
      // Gen AI filter requires unnesting to show features, so clearing it should return to nested view
      dispatch(actions.setUnNestAll(false));
      dispatch(actions.clearPreviousUnNestAllState());

      // ✅ CRITICAL: Clear isUnNested preference from localStorage
      // This allows Roadmap.tsx useEffect to properly re-nest when filters are cleared
      removeUserPreferenceField('isUnNested');
    }
  };

  // Handler for Category-Product hierarchical filter
  const handleCategoryProductChange = (
    categories: string[],
    products: string[]
  ) => {
    // ✅ Clear Must Win dropdown special UI when regular filters are modified
    clearMustWinState();

    // Update the categoryProductFilter state
    dispatch(actions.setCategoryProductSelection({ categories, products }));

    // Also update the existing category and product filters for backwards compatibility
    // This ensures the filtering logic in Roadmap.tsx works correctly
    dispatch(actions.setSelectedCats(categories));
    dispatch(actions.setSelectedProducts(products));

    // Save preferences
    saveUserPreferences({
      categoryProductCategories: categories,
      categoryProductProducts: products,
      categories: categories,
      products: products
    });

    // Call hierarchy filters API to update valid options
    const currentSelections = {
      productGroups: productGroups.selectedGroups,
      customerAudiences: audiences.selectedAudiences,
      categories: categories,
      products: products
    };
    // dispatch(actions.loadHierarchyFilters(currentSelections));
  };

  const FunnelIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="17"
      viewBox="0 0 16 17"
      fill="none"
    >
      <path
        d="M6.99901 16.7285C6.63963 16.7285 6.28221 16.6304 5.9619 16.4356C5.35936 16.0698 4.99999 15.4307 4.99999 14.7261V8.49024C4.99999 8.3042 4.93163 8.12647 4.80761 7.98878L0.627921 3.34521C0.0898308 2.75488 -0.0459092 1.92871 0.279291 1.19287C0.603511 0.45703 1.30468 0 2.10937 0H13.8906C14.6953 0 15.3965 0.45703 15.7207 1.19287C16.0459 1.92871 15.9102 2.75488 15.3682 3.34863L11.1924 7.98877C11.0684 8.12647 11 8.3042 11 8.49023V13.5356C11 14.3794 10.5352 15.1445 9.78711 15.5327L7.92188 16.501C7.62989 16.6528 7.31346 16.7285 6.99901 16.7285ZM2.10937 1.5C1.81445 1.5 1.6914 1.7085 1.65136 1.79834C1.61132 1.88818 1.54101 2.12012 1.73925 2.33789L5.92284 6.98486C6.29491 7.39843 6.49999 7.9331 6.49999 8.49023V14.7261C6.49999 14.98 6.66796 15.1094 6.74022 15.1533C6.81249 15.1978 7.00584 15.2856 7.23045 15.1699L9.09568 14.2012C9.34568 14.0718 9.49998 13.8169 9.49998 13.5356V8.49023C9.49998 7.9331 9.70506 7.39843 10.0771 6.98486L14.2568 2.34131C14.459 2.12012 14.3887 1.88819 14.3486 1.79834C14.3086 1.70849 14.1855 1.5 13.8906 1.5H2.10937Z"
        fill="#292929"
      />
    </svg>
  );
  return (
    <MenuTrigger>
      <ActionButton
        data-testid="funnel-filter-trigger"
        aria-label="Filter Options"
        UNSAFE_className={styles['funnel-filter-button']}
      >
        <FunnelIcon />
      </ActionButton>
      <Menu
        UNSAFE_className={styles['filter_container']}
        data-filter="filter"
        onAction={key => {
          // ✅ Clear Must Win dropdown special UI when regular filters are modified
          clearMustWinState();

          // Handle Revenue Impact toggle click
          if (key === 'revenue-impact') {
            const isCurrentlySelected =
              selectedRevenueImpactFilters instanceof Set &&
              selectedRevenueImpactFilters.has('revenue-impact-yes');

            if (isCurrentlySelected) {
              // Remove the filter
              setSelectedRevenueImpactFilters(new Set([]));
              dispatch(actions.setRevenueImpactFilters([]));
              saveUserPreferences({ revenueImpactFilters: [] });
            } else {
              // Add 'revenue-impact-yes' filter
              const newSelection = new Set(['revenue-impact-yes']);
              setSelectedRevenueImpactFilters(newSelection);
              dispatch(actions.setRevenueImpactFilters(['revenue-impact-yes']));
              saveUserPreferences({
                revenueImpactFilters: ['revenue-impact-yes']
              });
            }
          }
        }}
      >
        <SubmenuTrigger>
          <Item key="lanesTiers" data-lane="lanes">
            <Text>Lanes / Tiers {lanesTiersCountDisplay}</Text>
          </Item>
          <Menu
            data-testid="filter-section-lanes-tiers-menu"
            selectedKeys={selectedFilterOptions}
            onSelectionChange={handleFilterChange}
            selectionMode="multiple"
            UNSAFE_className={styles['subMenu']}
          >
            {lanesTiersFilterOptions.length ? (
              lanesTiersFilterOptions.map(option => (
                <Item
                  key={option.key}
                  data-testid={`filter-section-lanes-tiers-option-${option.key}`}
                >
                  <Text UNSAFE_className={styles.menuItemText}>
                    <MenuCheckbox
                      isSelected={
                        selectedFilterOptions instanceof Set
                          ? selectedFilterOptions.has(option.key)
                          : false
                      }
                    />
                    <span className={styles.menuItemLabel} title={option.label}>
                      {option.label}
                    </span>
                  </Text>
                </Item>
              ))
            ) : (
              <Item key="no-lanes-tiers">
                <Text>No Lanes/Tiers Available</Text>
              </Item>
            )}
          </Menu>
        </SubmenuTrigger>

        <Item key="divider">
          <Text>
            <Divider
              orientation="horizontal"
              UNSAFE_className={styles.divider}
            />
          </Text>
        </Item>
        <SubmenuTrigger>
          <Item key="segment">
            <Text>Customer Audience {segmentCountDisplay}</Text>
          </Item>
          <Menu
            data-testid="filter-section-audiences-menu"
            selectedKeys={computedAudienceKeys}
            onSelectionChange={handleAudienceChange}
            onAction={key => {
              // Handle "All" click specifically
              if (key === 'all') {
                const currentAudiences =
                  selectedAudiences instanceof Set
                    ? selectedAudiences
                    : new Set([]);
                const enabledIndividualOptions = audienceOptions.filter(
                  opt => opt.value !== 'all' && !opt.disabled
                );
                const enabledIndividualValues = enabledIndividualOptions.map(
                  opt => opt.value
                );

                // Check if all individual items are currently selected (conceptually "All" is selected)
                const allIndividualSelected =
                  enabledIndividualValues.length > 0 &&
                  enabledIndividualValues.every(val =>
                    currentAudiences.has(val)
                  );

                if (allIndividualSelected || currentAudiences.has('all')) {
                  // Set protection flag first
                  setUserClickedAll({
                    filter: 'audiences',
                    action: 'deselect'
                  });
                  userClickedAllRef.current = {
                    filter: 'audiences',
                    action: 'deselect',
                    timestamp: Date.now()
                  };

                  // Force clear all selections
                  setSelectedAudiences(new Set([]));
                  dispatch(actions.setSelectedAudiences([]));
                  saveUserPreferences({ audiences: [] });
                  saveUserPreferences({ audienceLabels: [] });

                  // Clear protection flag after delay
                  setTimeout(() => {
                    setUserClickedAll(null);
                    userClickedAllRef.current = null;
                  }, 1000);
                }
              }
            }}
            selectionMode="multiple"
            // disabledKeys={[
            //   'no-audiences',
            //   ...audienceOptions
            //     .filter(option => option.disabled)
            //     .map(option => option.value)
            // ]}
            UNSAFE_className={styles['subMenu']}
          >
            {audienceOptions.length ? (
              audienceOptions.map(
                (option: { label: string; value: string }) => (
                  <Item
                    key={option.value}
                    data-testid={`filter-section-audience-${option.value
                      .replace(/[^a-zA-Z0-9]/g, '-')
                      .toLowerCase()}`}
                  >
                    <Text UNSAFE_className={styles.menuItemText}>
                      <View>
                        <MenuCheckbox
                          isSelected={
                            computedAudienceKeys instanceof Set
                              ? computedAudienceKeys.has(option.value)
                              : false
                          }
                          isDisabled={(option as any).disabled}
                        />
                      </View>
                      <span
                        className={styles.menuItemLabel}
                        title={option.label}
                      >
                        {option.label}
                      </span>
                    </Text>
                  </Item>
                )
              )
            ) : (
              <Item key="no-audiences">
                <Text>No Audiences</Text>
              </Item>
            )}
          </Menu>
        </SubmenuTrigger>
        <SubmenuTrigger>
          <Item key="productGroup">
            <Text>Product Group {productGroupCountDisplay}</Text>
          </Item>
          <Menu
            data-testid="filter-section-product-groups-menu"
            selectedKeys={computedProductGroupKeys}
            onSelectionChange={handleGroupChange}
            UNSAFE_className={styles['subMenu']}
            onAction={key => {
              // Handle "All" click specifically
              if (key === 'all') {
                const currentProductGroups =
                  selectedProductGroups instanceof Set
                    ? selectedProductGroups
                    : new Set([]);
                const enabledIndividualOptions = groupOptions.filter(
                  opt => opt.value !== 'all' && !opt.disabled
                );
                const enabledIndividualValues = enabledIndividualOptions.map(
                  opt => opt.value
                );

                // Check if all individual items are currently selected (conceptually "All" is selected)
                const allIndividualSelected =
                  enabledIndividualValues.length > 0 &&
                  enabledIndividualValues.every(val =>
                    currentProductGroups.has(val)
                  );

                if (allIndividualSelected || currentProductGroups.has('all')) {
                  // Set protection flag first
                  setUserClickedAll({
                    filter: 'productGroups',
                    action: 'deselect'
                  });
                  userClickedAllRef.current = {
                    filter: 'productGroups',
                    action: 'deselect',
                    timestamp: Date.now()
                  };

                  // Force clear all selections
                  setSelectedProductGroups(new Set([]));
                  dispatch(actions.setSelectedGroups([]));
                  saveUserPreferences({ productGroups: [] });
                  saveUserPreferences({ productGroupLabels: [] });

                  // Clear dependent data
                  dispatch(actions.catgoriesLoaded([]));
                  dispatch(actions.setSelectedEvents([]));
                  dispatch(
                    actions.updateHighlightedIssue({ relatedIssues: [] })
                  );

                  // Clear protection flag after delay
                  setTimeout(() => {
                    setUserClickedAll(null);
                    userClickedAllRef.current = null;
                  }, 1000);
                }
              }
            }}
            selectionMode="multiple"
            // disabledKeys={[
            //   'no-product-groups',
            //   ...groupOptions
            //     .filter(option => option.disabled)
            //     .map(option => option.value)
            // ]}
          >
            {groupOptions.length ? (
              groupOptions.map((option: { label: string; value: string }) => (
                <Item
                  key={option.value}
                  data-testid={`filter-section-product-group-${option.value
                    .replace(/[^a-zA-Z0-9]/g, '-')
                    .toLowerCase()}`}
                >
                  <Text UNSAFE_className={styles.menuItemText}>
                    <View>
                      <MenuCheckbox
                        isSelected={
                          computedProductGroupKeys instanceof Set
                            ? computedProductGroupKeys.has(option.value)
                            : false
                        }
                        isDisabled={(option as any).disabled}
                      />
                    </View>
                    <span className={styles.menuItemLabel} title={option.label}>
                      {option.label}
                    </span>
                  </Text>
                </Item>
              ))
            ) : (
              <Item key="no-product-groups">
                <Text>No Product Groups</Text>
              </Item>
            )}
          </Menu>
        </SubmenuTrigger>
        <SubmenuTrigger>
          <Item key="categoryProduct">
            <Text>{categoryProductCountDisplay}</Text>
          </Item>
          <CategoryProductFilter
            categoryToProductMap={categoryToProductMap}
            selectedCategories={
              new Set(categoryProductFilter.selectedCategories)
            }
            selectedProducts={new Set(categoryProductFilter.selectedProducts)}
            onSelectionChange={handleCategoryProductChange}
          />
        </SubmenuTrigger>

        {!hideEventsFilter ? (
          <SubmenuTrigger>
            <Item key="events">
              <Text>Events {eventsCountDisplay}</Text>
            </Item>
            <EventsFilter
              eventOptions={eventOptions}
              selectedEventTitles={selectedeventsLabel as Selection}
              onSelectionChange={handleEventChange}
              onAction={key => {
                // Handle "All" click specifically (note: Events uses "All" not "all")
                if (key === 'All') {
                  const currentEvents =
                    selectedeventsLabel instanceof Set
                      ? selectedeventsLabel
                      : new Set([]);
                  const individualOptions = eventOptions.filter(
                    opt => opt.title !== 'All'
                  );
                  const individualTitles = individualOptions.map(
                    opt => opt.title
                  );

                  // Check if all individual items are currently selected (conceptually "All" is selected)
                  const allIndividualSelected =
                    individualTitles.length > 0 &&
                    individualTitles.every(title => currentEvents.has(title));

                  if (allIndividualSelected || currentEvents.has('All')) {
                    // Set protection flag first
                    setUserClickedAll({ filter: 'events', action: 'deselect' });
                    userClickedAllRef.current = {
                      filter: 'events',
                      action: 'deselect',
                      timestamp: Date.now()
                    };

                    // Force clear all selections
                    setSelectedeventsLabel(new Set([]));
                    setEventValue([]);
                    dispatch(actions.setSelectedEvents([]));
                    saveUserPreferences({ events: [] });
                    saveUserPreferences({ eventsLabel: [] });

                    // Clear highlighted issues
                    dispatch(
                      actions.updateHighlightedIssue({ relatedIssues: [] })
                    );

                    // Clear protection flag after delay
                    setTimeout(() => {
                      setUserClickedAll(null);
                      userClickedAllRef.current = null;
                    }, 1000);
                  }
                }
              }}
            />
          </SubmenuTrigger>
        ) : null}
        <Item key="divider">
          <Text>
            <Divider
              orientation="horizontal"
              UNSAFE_className={styles.divider}
            />
          </Text>
        </Item>
        <SubmenuTrigger>
          <Item key="surface">
            <Text>Surface {surfaceCountDisplay}</Text>
          </Item>
          <Menu
            data-testid="filter-section-features-menu"
            onSelectionChange={handleFeatureChange}
            UNSAFE_className={styles['subMenu']}
            onAction={key => {
              // Handle "All" click specifically
              if (key === 'all') {
                const currentFeatures =
                  selectedFeaturesValues instanceof Set
                    ? selectedFeaturesValues
                    : new Set([]);
                const individualOptions = featureOptions.filter(
                  opt => opt.value !== 'all'
                );
                const individualValues = individualOptions.map(
                  opt => opt.value
                );

                // Check if all individual items are currently selected (conceptually "All" is selected)
                const allIndividualSelected =
                  individualValues.length > 0 &&
                  individualValues.every(value => currentFeatures.has(value));

                if (allIndividualSelected || currentFeatures.has('all')) {
                  // Set protection flag first
                  setUserClickedAll({ filter: 'features', action: 'deselect' });
                  userClickedAllRef.current = {
                    filter: 'features',
                    action: 'deselect',
                    timestamp: Date.now()
                  };

                  // Force clear all selections
                  setSelectedFeaturesValues(new Set([]));
                  dispatch(actions.setSelectedFeatures([]));
                  saveUserPreferences({ features: [] });

                  // Clear protection flag after delay
                  setTimeout(() => {
                    setUserClickedAll(null);
                    userClickedAllRef.current = null;
                  }, 1000);
                }
              }
            }}
            selectionMode="multiple"
            selectedKeys={computedFeatureKeys}
            // disabledKeys={['no-features']}
          >
            {featureOptions.length ? (
              featureOptions.map((option: { label: string; value: string }) => (
                <Item
                  key={option.value}
                  data-testid={`filter-section-feature-${option.value
                    .replace(/[^a-zA-Z0-9]/g, '-')
                    .toLowerCase()}`}
                >
                  <Text UNSAFE_className={styles.menuItemText}>
                    <View>
                      <MenuCheckbox
                        isSelected={
                          computedFeatureKeys instanceof Set
                            ? computedFeatureKeys.has(option.value)
                            : false
                        }
                        isDisabled={(option as any).disabled}
                      />
                    </View>
                    <span className={styles.menuItemLabel} title={option.label}>
                      {option.label}
                    </span>
                  </Text>
                </Item>
              ))
            ) : (
              <Item key="no-features">
                <Text>No Features</Text>
              </Item>
            )}
          </Menu>
        </SubmenuTrigger>

        <SubmenuTrigger>
          <Item key="gen-ai" data-lane="gen-ai">
            <Text>GenAi</Text>
          </Item>
          <Menu
            data-testid="filter-section-gen-ai-menu"
            selectionMode="multiple"
            selectedKeys={selectedGenAiFilters}
            onSelectionChange={handleGenAiFilterChange}
            UNSAFE_className={styles['subMenu']}
          >
            <Item
              key={'gen-ai-all-features'}
              data-testid={`filter-section-gen-ai-all-features`}
            >
              <Text UNSAFE_className={styles.menuItemText}>
                <MenuCheckbox
                  isSelected={
                    selectedGenAiFilters instanceof Set
                      ? selectedGenAiFilters.has('gen-ai-all-features')
                      : false
                  }
                />
                <span
                  className={styles.menuItemLabel}
                  title="GenAi - All Features"
                >
                  GenAi - All Features
                </span>
              </Text>
            </Item>
            <Item
              key={'gen-ai-consumes-credits'}
              data-testid={`filter-section-gen-ai-consumes-credits`}
            >
              <Text UNSAFE_className={styles.menuItemText}>
                <MenuCheckbox
                  isSelected={
                    selectedGenAiFilters instanceof Set
                      ? selectedGenAiFilters.has('gen-ai-consumes-credits')
                      : false
                  }
                />
                <span
                  className={styles.menuItemLabel}
                  title="GenAi - Consumes Credits"
                >
                  GenAi - Consumes Credits
                </span>
              </Text>
            </Item>
          </Menu>
        </SubmenuTrigger>
        <SubmenuTrigger>
          <Item key="status" data-lane="status">
            <Text>Status {statusCountDisplay}</Text>
          </Item>
          <StatusFilter
            statusOptions={statusFilterOptions}
            selectedStatusKeys={combinedStatusKeys}
            onSelectionChange={handleStatusChange}
            onAction={key => {
              // Handle "All" click specifically
              if (key === 'all') {
                const currentStatus =
                  selectedStatusOptions instanceof Set
                    ? selectedStatusOptions
                    : new Set([]);
                const individualOptions = statusFilterOptions.filter(
                  opt => opt.key !== 'all'
                );
                const individualKeys = individualOptions.map(opt => opt.key);

                // Check if all individual items are currently selected (conceptually "All" is selected)
                const allIndividualSelected =
                  individualKeys.length > 0 &&
                  individualKeys.every(key => currentStatus.has(key));

                if (allIndividualSelected || currentStatus.has('all')) {
                  // Force clear all selections
                  setSelectedStatusOptions(new Set([]));
                  saveUserPreferences({ status: [] });
                }
              }
            }}
          />
        </SubmenuTrigger>
        <SubmenuTrigger>
          <Item key="moment-type" data-lane="moment-type">
            <Text>Moment Type{momentTypeCountDisplay}</Text>
          </Item>
          <MomentTypeFilter
            selectedKeys={selectedMomentTypeOptions}
            onSelectionChange={handleMomentTypeChange}
            includeEolMomentType={
              !hideEolMomentType || milestonesGridMomentTypeMenu
            }
          />
        </SubmenuTrigger>
        <Item
          key="revenue-impact"
          data-lane="revenue-impact"
          data-testid="filter-section-revenue-impact"
        >
          <Text
            UNSAFE_className={`${styles.menuItemText} ${styles.menuItemWithCheckmark}`}
          >
            <span className={styles.menuItemLabel}>
              Impacts Revenue Targets
            </span>
            {selectedRevenueImpactFilters instanceof Set &&
              selectedRevenueImpactFilters.has('revenue-impact-yes') && (
                <View
                  UNSAFE_style={{
                    marginLeft: 'auto',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <CheckmarkIcon20
                    width={20}
                    height={20}
                    aria-label="Selected"
                  />
                </View>
              )}
          </Text>
        </Item>
      </Menu>
    </MenuTrigger>
  );
}

NewFilter.displayName = 'NewFilter';
